// Тонкий типизированный клиент API.

import type { CallInfo } from './chat/callCard';
import type { FunctionName, WorkState } from './functions/functions';

/**
 * Сколько замечаний сервер отдаёт одним списком. Значение должно совпадать с
 * REMARKS_PAGE в server/src/walks/remarks.service.ts: пришло ровно столько —
 * значит есть и старее, и реестр говорит об этом человеку.
 */
export const REMARKS_PAGE = 500;

export type PublicUser = {
  id: string;
  login: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  orgId: string;
  email: string | null;
  hasPin: boolean; // на аккаунте настроен пин-код быстрого входа
  birthday: string | null;
  avatarAt: string | null; // метка версии фотографии; пусто — её нет
};

/** Профиль: пользователь, должности, доступы и проекты, где он в команде. */
export type MobileMode = 'NONE' | 'FULL';

/** Возможность и её поведение на телефоне; приходит в профиле (server/src/capabilities.ts). */
export type CapabilityState = {
  code: string;
  module: string;
  /** Подраздел проекта, если возможность живёт внутри него. */
  section?: string;
  mode: MobileMode;
};

export type Profile = {
  user: PublicUser;
  positions: UserPositionView[];
  permissions: string[];
  memberProjectIds: string[];
  mobile: CapabilityState[];
  /** Назначения человека по реестру доступов; объединение действующих должностей. */
  grants: { code: string; level: string; scope: 'own' | 'all' }[];
};

export type ProcessLite = {
  code: string;
  name: string;
  summary: string;
  dsi: Record<string, string[]>;
  stageLabel: string;
  groupId: string;
  orderIdx: number;
  hasRegulation: boolean;
};

export type Bootstrap = {
  groups: { id: string; code: string; name: string; tag: string }[];
  stages: { groupId: string; num: string; name: string; cycle: string; orderIdx: number }[];
  processes: ProcessLite[];
  roles: { code: string; name: string; sub: string }[];
  terms: { abbr: string; definition: string }[];
};

export type ProcessFull = {
  code: string;
  name: string;
  summary: string;
  dsi: Record<string, string[]>;
  stageLabel: string;
  regulation: { html: string; version: string } | null;
};

export type CommentView = {
  id: number;
  login: string;
  name: string;
  admin: boolean;
  ts: number;
  text: string;
  replyTo: number | null;
  edited: boolean;
  reactions: Record<string, { login: string; name: string }[]>;
  /** Файл комментария: один, до 20 МБ (стандартный модуль комментариев). */
  file: { id: number; name: string; size: number } | null;
};

export type NotificationView = {
  id: number;
  type: string;
  read: boolean;
  ts: number;
  proc?: string;
  from?: string;
  fromLogin?: string;
  commentId?: number;
  /** Обсуждение стандартного модуля комментариев и подпись места («процесс «…»», «замечание №12 по модели»). */
  thread?: string;
  place?: string;
  // уведомления по задачам ГПР
  projectId?: string;
  projectName?: string;
  rowId?: string;
  task?: string;
  text?: string;
  // итоги звонка с доской
  callId?: string;
  /** Сколько листов доски осталось после звонка. */
  count?: number;
  /** Сколько сообщений в переписке звонка: у звонка без карточки это единственный путь к ней. */
  messages?: number;
  // календарь: приглашение/правка/отмена встречи и напоминание о ней
  eventId?: string;
  /** Название встречи. */
  title?: string;
  startsAt?: string;
  // задания: что назначено, куда ведёт и сколько рабочих дней осталось
  assignmentId?: string;
  typeName?: string;
  dueIn?: string;
  /** Что случилось со встречей, по-русски и по существу: «назначена»,
   * «перенесена: 10:00 → 14:00», «отменена: вся серия», «изменена: переговорная:
   * «Зал А» → «Зал Б»» и т. п. — готовая строка для показа после слова «встреча». */
  what?: string;
  // договор: возврат, подпись, новая версия, отмена
  contractId?: string;
  profileId?: string;
  subject?: string;
  contractorName?: string;
  // «Вас добавили в команду»: работа — `projectId`, объект — `projectName`, функция и должность
  fn?: string;
  position?: string;
};

export type ProjectStatus = 'DRAFT' | 'ACTIVE' | 'DONE' | 'ARCHIVED';

export type PackageType = 'ESTIMATE' | 'TZ' | 'OWN';
export type PackageStatus = 'PREP' | 'READY' | 'RUNNING' | 'SUSPENDED' | 'FINISHED' | 'CLOSING' | 'CLOSED' | 'SPLIT';
export type PackagePhaseView = {
  key: 'prep' | 'exec' | 'close';
  label: string;
  start: string;
  end: string;
  baseStart: string | null;
  baseEnd: string | null;
  actualStart: string | null;
  actualEnd: string | null;
};
export type PackageSuspension = { id: string; from: string; to: string | null; reason: string; comment: string; by: string };
export type PackageEvent = { at: string; by: string; kind: string; text: string };
/** Строительный пакет — вид на строку ГПР типа «package». */
export type PackageView = {
  rowId: string;
  code: string | null;
  name: string;
  section: string | null;
  packageType: PackageType | null;
  budget: number | null;
  owner: string;
  contractor: string;
  contract: string;
  parentId: string | null;
  status: PackageStatus;
  statusLabel: string;
  phase: 'prep' | 'exec' | 'close' | null;
  phases: PackagePhaseView[];
  deviationDays: number | null;
  idleDays: number;
  milestones: { contractSignedAt: string | null; readyAt: string | null; finishedAt: string | null; closedAt: string | null };
  suspensions: PackageSuspension[];
  events: PackageEvent[];
};
export type PackagePatch = {
  packageType?: PackageType | null;
  owner?: string;
  contractor?: string;
  contract?: string;
  budget?: number | null;
  contractSignedAt?: string | null;
  readyAt?: string | null;
  closedAt?: string | null;
};
export type PackageSection = { id: string; code: string; name: string; hidden: boolean; packages: number };

/** Карточка подрядчика в списке реестра. */
export type ContractorRow = {
  id: string;
  name: string;
  inn: string;
  legalForm: LegalForm;
  status: ContractorStatus;
  score: number;
  region: string;
  /** `admission` — допуск вида работ по действующим проверкам, тем же расчётом, что схема проверки; у записи без направления — `null`. */
  skills: { id: string; code: string; name: string; parentId: string | null; admission: CategoryAdmission['state'] | null }[];
};

export type LegalForm = 'LLC' | 'JSC' | 'SOLE' | 'SELF' | 'FOREIGN';

export type ContractorStatus =
  | 'DRAFT' | 'LISTED' | 'UNCHECKED' | 'CHECKING' | 'ALLOWED' | 'LIMITED' | 'EXPIRED' | 'SUSPENDED' | 'BLOCKED';

export type ContractorContact = {
  id: string;
  name: string;
  position: string;
  email: string;
  phone: string;
  isPrimary: boolean;
};

export type ContractorFile = {
  id: string;
  kind: string;
  name: string;
  mime: string;
  size: number;
  validTo: string | null;
  createdAt: string;
};

export type ContractorCardView = ContractorRow & {
  orgId: string;
  /** Роли компании: хранимые плюс выведенные (копия server/src/counterparties/counterparties.model.ts). */
  roles: CounterpartyRole[];
  stored: StoredRole[];
  /** Наша компания: юрлицо группы, реквизиты ведутся в «Наших юрлицах». */
  own: boolean;
  /** Юрлицо группы у нашей компании: по нему правка и удаление со страницы юрлица; у чужой — `null`. */
  legalEntityId: string | null;
  /** Код в номере договора нашей компании; у чужой и у нашей без кода — пусто. */
  legalCode: string;
  /** Полное наименование и юридический адрес нашей компании по записи юрлица; у чужой — пусто. */
  legalFullName: string;
  legalAddress: string;
  /** Фонды, которыми организация управляет как УК. */
  managed: { orgId: string; name: string }[];
  ogrn: string;
  kpp: string;
  site: string;
  notes: string;
  /** Заведение не закончено, и карточку завёл этот человек: до «Готово» он правит досье, контакты и виды работ без своих этапов. */
  myIntake: boolean;
  /** Налоговый режим: пусто значит «нет данных», а не общий режим. */
  taxMode: string;
  /** Дата, на которую режим известен: набор налоговой отстаёт на месяц. */
  taxModeAt: string | null;
  /** Ставка НДС: отчёт проверки её не называет, называет человек. */
  vatRate: number | null;
  contacts: ContractorContact[];
  skills: (ContractorRow['skills'][number] & { claimed: boolean })[];
  files: ContractorFile[];
  missingDocs: { kind: string; label: string }[];
  requiredDocs: { kind: string; label: string }[];
  events: {
    id: string;
    kind: string;
    text: string;
    actor: string;
    /** Имя и должность автора: в истории человек должен быть человеком. */
    actorName: string;
    actorPosition: string;
    createdAt: string;
  }[];
};

/** Блок проверки подрядчика с пунктами чек-листа. */
export type CheckKind = 'LEGAL' | 'FINANCE' | 'CAPABILITY';
export type FactorState = 'UNKNOWN' | 'GREEN' | 'YELLOW' | 'RED';

/** Данные критерия: получены, ещё едут или источник их не дал. */
export type DataState = 'READY' | 'WAITING' | 'NO_DATA';

export type CheckComment = {
  id: string;
  author: string;
  text: string;
  createdAt: string;
  files: { id: string; name: string; mime: string; size: number }[];
};

export type CheckItem = {
  id: string;
  code: string;
  /** Смысловой блок внутри проверки: «Правоспособность», «Долги и взыскания». */
  group: string;
  title: string;
  green: string;
  yellow: string;
  red: string;
  weight: number;
  stop: boolean;
  auto: boolean;
  hint: string;
  source: string;
  state: FactorState;
  autoState: FactorState;
  /** Человеческое значение: «8 % выручки», «24 года». */
  value: string;
  dataState: DataState;
  /** Почему данных нет: источник не ответил, не опрашивался, блока нет. */
  dataNote: string;
  overrideReason: string;
  comments: CheckComment[];
};

/** Отчёт по контрагенту: общий блок на странице проверки. */
export type ReportView = {
  id: string;
  state: 'PENDING' | 'READY' | 'ERROR';
  dataDate: string | null;
  receivedAt: string | null;
  error: string;
};

/** Короткая запись о попытке: для схемы и списка действующих проверок. */
export type CheckBrief = {
  id: string;
  kind: CheckKind;
  attempt: number;
  state: 'WAITING' | 'RUNNING' | 'DONE' | 'EXPIRED';
  score: number;
  coverage: number;
  color: 'GREEN' | 'YELLOW' | 'RED' | null;
  verdict: string;
  reason: string;
  doneAt: string | null;
  validTo: string | null;
  progress: { rated: number; total: number };
  assignment: CheckAssignment | null;
};

/** Исполнитель проверки: имя и должность, как на схеме. */
export type CheckAssignment = {
  id: string;
  userId: string | null;
  dueAt: string | null;
  state: string;
  userName: string;
  positionName: string;
};

/** Проверки компании: действующий результат и активная попытка по каждому блоку. */
/** Состояние точки процесса: перехода к производственным проверкам и допуска. */
export type FlowGate = { state: 'WAITING' | 'ALLOWED' | 'BLOCKED'; note: string };

/**
 * Допуск по одному заявленному виду работ: он и есть ответ на вопрос «на что
 * подрядчик допущен». `checkId` — производственная проверка, которая его
 * покрывает: своя или, пока своей нет, проверка направления.
 */
export type CategoryAdmission = {
  categoryId: string;
  name: string;
  state: FlowGate['state'];
  note: string;
  validTo: string | null;
  checkId: string | null;
  /** Допуск держится на проверке направления, а не на своей проверке вида. */
  byDirection: boolean;
};

export type ChecksView = {
  primary: boolean;
  blocks: {
    kind: CheckKind;
    directionId: string | null;
    title: string;
    current: CheckBrief | null;
    active: CheckBrief | null;
  }[];
  transition: FlowGate;
  admissions: CategoryAdmission[];
  report: ReportView | null;
};

/** Страница одной проверки. */
export type CheckView = {
  id: string;
  kind: CheckKind;
  attempt: number;
  reason: string;
  state: 'WAITING' | 'RUNNING' | 'DONE' | 'EXPIRED';
  title: string;
  company: { id: string; name: string; inn: string };
  assignment: CheckAssignment | null;
  verdict: string;
  doneAt: string | null;
  validTo: string | null;
  items: CheckItem[];
  score: number;
  coverage: number;
  /** Сколько веса осталось без данных: по нему объясняем, почему нельзя закрыть. */
  missingWeight: number;
  report: ReportView | null;
};

// ---------- договоры ----------

/*
 * Договор подрядчика. Типы — копия видов сервера
 * (server/src/contracts/contracts.model.ts и contracts.view.ts): даты там
 * уходят в ответ через `toISOString`, поэтому здесь они строки.
 */

export type ContractStatus =
  | 'DRAFT' | 'COMMENTING' | 'EDITING' | 'VISAS' | 'UK_SIGNING' | 'SIGNING' | 'SCAN' | 'SIGNED' | 'CANCELLED';

export type ContractAction =
  | 'send' | 'closeComments' | 'uploadVersion' | 'sendVersion' | 'toVisas' | 'visa' | 'final' | 'signWay'
  | 'ukReply' | 'ukSign'
  | 'sign' | 'scan' | 'cancel' | 'openGuest' | 'closeGuest' | 'openUkGuest' | 'closeUkGuest';

export type ContractRole = 'admin' | 'initiator' | 'holder' | 'visa' | 'signer' | 'scanner' | 'reader';

/**
 * Требование процесса: то, без чего договор не двинется дальше. Копия списка
 * сервера (`CONTRACT_NEEDS` в contracts.model.ts) и порядок тот же —
 * порядок хода.
 */
export type ContractNeed = 'out' | 'ukReply' | 'round' | 'signWay' | 'visas' | 'ukSign' | 'sign' | 'scan';

/** Требование и его ветки: действия, которыми этот человек его закроет. */
export type ContractNeedRow = { key: ContractNeed; acts: ContractAction[] };

/** Исполнитель этапа договора: задание, его состояние и кто его ведёт. */
export type Doer = {
  assignmentId: string;
  state: string;
  userId: string | null;
  userName: string;
  positionName: string;
  dueAt: string | null;
  result: string;
};

/** Платёж календаря договора. */
export type ContractPaymentView = { id: string; amount: string; date: string; kind: PaymentKind; orderIdx: number };

/**
 * Платёж действующего календаря на свою дату (`TermsPaymentView` сервера):
 * сумма по режиму этого дня, режим, ставка и налог; `was` — сумма как
 * введена, когда смена режима её пересчитала.
 */
export type ContractTermsPaymentView = ContractPaymentView & {
  /** Строка отмечена «регулярной»: в реестр приходит без заявки (спека 2026-10-07). Нет у ответа сервера старше этой работы. */
  regular?: boolean;
  was: string | null;
  mode: string;
  rate: number;
  vat: string;
  /** Цена без НДС: платёж минус налог. */
  net: string;
};

/**
 * Налоговый режим договора с даты (`TaxRegime` в
 * server/src/contracts/intake.model.ts): у исходного `from` пусто.
 */
export type TaxRegime = { mode: string; rate: number; from: string | null };

/** Назначение платежа: четыре значения, и других не бывает. */
export type PaymentKind = 'ADVANCE' | 'INTERIM' | 'FINAL' | 'RETENTION';

/**
 * Соседний договор ссылкой: чем назван и куда вести. `no` — порядковый номер
 * допсоглашения внутри рамочного договора; у рамочного и у обычного договора
 * его нет, и там он ноль.
 */
/** `number` — номер по бумаге; пусто, пока его не присвоили на визах. */
export type ContractRef = { id: string; seq: number; number: string; subject: string; no: number };

/**
 * Род допсоглашения: ДС на новый предмет или ДС об изменении. Объявляется при
 * заведении и из глубины связи не выводится (`AMEND_KINDS` в
 * server/src/contracts/intake.model.ts).
 */
export type AmendKind = 'WORK' | 'CHANGE';

/** Юрлицо в коротком списке для форм. */
export type LegalOption = {
  id: string;
  /** Организация юрлица: сторона-контрагент не может быть тем же юрлицом, что наша сторона. */
  orgId: string;
  name: string;
  fullName: string;
  form: OrgFormName;
  /** ИНН организации; нет — пустая строка. */
  inn: string;
  manager: { orgId: string; name: string } | null;
};

/**
 * Роли нашего юрлица для чипов (спека 2026-10-01): собственник — заведён
 * собственником; остальные — хоть раз исполнитель работы такой функции.
 * Порядок — как у вкладок «Функций».
 */
export type OurRoles = { legalEntityId: string; orgId: string; roles: FunctionName[] }[];

/** Допсоглашение в списке семьи договора. */
export type ContractAmendRow = ContractRef & {
  status: ContractStatus;
  statusLabel: string;
  /** Своя записанная сумма: у ДС на новый предмет — исходная. */
  amount: string;
  /** Своя записанная сумма без НДС: ею звено занимает лимит рамочного. */
  net: string;
  /** Действующая сумма: исходная, изменённая подписанными ДС об изменении. */
  effective: string;
  /** Действующая цена без НДС (`net` из `shownTerms` звена). */
  effectiveNet: string;
  /** Записано ли звено с НДС: иначе строка показывает цену без НДС. */
  vatIncluded: boolean;
  /** Что оно изменяет — ближайшее звено; у ДС на новый предмет пусто. */
  amends: ContractRef | null;
  /**
   * Основание цепочки изменений: ДС на новый предмет, которое в итоге
   * изменяется. Пусто, когда изменяется сам договор, и у ДС на новый предмет.
   */
  base: ContractRef | null;
  /** Какие условия ДС об изменении объявило изменёнными; пусто — формулировки. */
  changes: string[];
  /** Занимает ли оно лимит: изменение вытесняет изменяемое, черновик не в счёт. */
  counted: boolean;
  /** Чем оно объявлено при заведении; у договора семьи пусто. */
  kind: AmendKind | '';
  /** Те же сведения, что у строки реестра договоров: список один. */
  round: number;
  holderName: string;
  daysInStatus: number;
};

/**
 * Действующие условия договора: исходные, поправленные его подписанными
 * допсоглашениями. `by` отвечает на вопрос «на основании чего»: имя условия →
 * допсоглашение, чьей властью оно таково.
 */
export type ContractTermsView = {
  /** Действующая сумма на сегодня: с календарём сходится с ним. */
  amount: string;
  /** Сумма как введена, когда смена режима её пересчитала; иначе пусто. */
  amountWas: string | null;
  vatRate: number;
  vat: string;
  /** Цена без НДС действующей суммы: сумма минус налог. */
  net: string;
  taxMode: string;
  taxModeAt: string | null;
  validTo: string | null;
  payments: ContractTermsPaymentView[];
  by: Record<string, { id: string; no: number; label: string }>;
  /** Налоговая история семьи: режим договора и открытые его ДС об изменении, каждый с дня подписания. */
  taxHistory: TaxRegime[];
};

/** Тип договора — он же первый уровень классификатора (`CONTRACT_TYPES` в server/src/contractors/contract-types.model.ts). */
export type ContractType =
  | 'BUILD'
  | 'PIR'
  | 'SUPPLY'
  | 'SERVICES'
  | 'MACHINERY'
  | 'PROPERTY'
  | 'CARRIAGE'
  | 'AGENCY'
  | 'DESIGN_BUILD'
  | 'SALE'
  | 'LICENSE'
  | 'NDA'
  | 'LOAN'
  | 'ASSIGNMENT'
  | 'GIFT'
  | 'INSURANCE'
  | 'TERM_SHEET'
  | 'INVESTMENT'
  | 'OTHER';

/** Назначение договора (`PURPOSES` в server/src/contracts/purpose.model.ts). */
export type Purpose = 'BUY' | 'SELL' | 'OTHER';

/** Набор закрывающих (`CLOSING_SETS` в server/src/contractors/contract-types.model.ts). */
export type ClosingSet =
  | 'KS'
  | 'WORKS_ACT'
  | 'SERVICES_ACT'
  | 'PIR_ACT'
  | 'SUPPLY'
  | 'MACHINERY'
  | 'PROPERTY'
  | 'CARRIAGE'
  | 'AGENT'
  | 'TRANSFER'
  | 'DESIGN_BUILD';

/**
 * Как номер присваивается на шаге способа подписания (`NumberingMode` в
 * server/src/contracts/intake.model.ts): сам, руками или на выбор «Наш номер /
 * Номер УК».
 */
export type NumberingMode = 'AUTO' | 'MANUAL' | 'CHOICE';

/**
 * Приглашённый к договору письмом (спека 2026-10-06-mail-invites-design.md):
 * «приглашён» — письмо ушло, «вошёл» — открыл ссылку хоть на одном
 * устройстве, «убран» — держатель его убрал.
 */
export type ContractInviteRow = {
  id: string;
  name: string;
  email: string;
  position: string;
  state: 'INVITED' | 'JOINED' | 'REMOVED';
  joinedAt: string | null;
  sentAt: string | null;
};
/** Итог приглашения: письмо ушло (`link` пуст) или ссылка, которую передать самому. */
export type ContractInviteResult = { card: ContractCardView; link: string; sent: boolean };
export type ContractInviteInput = { side: 'PARTY' | 'UK'; name: string; email: string; position: string };

export type ContractCardView = {
  id: string;
  /** Внутренний номер: выдан при заведении и не меняется. */
  seq: number;
  /**
   * Сторона договора: контрагент, у старых — организация подрядчика.
   * `profileId` есть у закупки у стороннего подрядчика.
   */
  party: { orgId: string; name: string; profileId: string | null };
  /** Назначение: у старых пусто и читается закупкой. */
  purpose: Purpose;
  /** Обе стороны — юрлица группы: ссылки наружу нет. */
  intraGroup: boolean;
  /** Предмет текстом; пусто, когда предмет — вид работ. */
  subjectText: string;
  legalEntity: { id: string; name: string };
  /** Договор без проекта разрешён. */
  project: { id: string; code: string; name: string } | null;
  /** Тип договора: слепок при заведении, у старых — тип направления. */
  contractType: ContractType | null;
  kindLabel: string;
  /** Закрывающие: у ДС об изменении — закрывающие того звена цепочки, что их выбрало. */
  closing: ClosingSet | null;
  closingLabel: string;
  /** Предмет видом работ: у рамочного направление, у прочих вид работ. Пусто, когда предмет — текст. */
  category: { id: string; name: string; parentId: string | null; parentName: string } | null;
  /**
   * Предметы «Проектирования и строительства»: вид работ, его направление и
   * закрывающие. Пусто у всех, кроме корня такого договора.
   */
  subjects: { categoryId: string; name: string; direction: string; closing: ClosingSet; closingLabel: string }[];
  subject: string;
  /**
   * Предмет семьи сейчас, когда он разошёлся с записанным здесь; совпали —
   * `null`. Записанный предмет подписанной правки задним числом не двигается
   * — карточка называет оба.
   */
  subjectNow: { subject: string; note: string } | null;
  /** Договор, чьим пакетом согласуется это допсоглашение; пусто у самостоятельного. */
  packageOf: ContractRef | null;
  /** Прежний свободный предмет переехавшего договора; у новых пусто. */
  note: string;
  framework: boolean;
  /** «В рамках чего»: рамочный договор допсоглашения. */
  parentId: string | null;
  /** «Что изменяет»: допсоглашение, которое это изменяет. */
  amendsId: string | null;
  /** «В рамках чего» ссылкой: у допсоглашения рамочный договор, иначе пусто. */
  parent: ContractRef | null;
  /** «Что изменяет» ссылкой: изменяемое допсоглашение, если оно есть. */
  amends: ContractRef | null;
  /**
   * Род этого допсоглашения: `WORK` — ДС на новый предмет, `CHANGE` — ДС об
   * изменении. У договора семьи пусто.
   */
  amendKind: AmendKind | '';
  /** Какие условия оно объявило изменёнными, названные словами. */
  amendChanges: { field: string; label: string }[];
  /**
   * Что висит на этом договоре: у корня семьи — все её допсоглашения обоих
   * уровней, у ДС на новый предмет — его изменения. У ДС об изменении пусто.
   */
  amendments: ContractAmendRow[];
  /**
   * «Итог с ДС» разового договора: своя действующая сумма плюс его ДС на
   * новый предмет. Пусто у рамочного, у допсоглашения и без обещанных ДС на предмет.
   */
  total: string | null;
  /** «Итог с ДС» ценой без НДС: те же слагаемые, что у `total`; пусто, когда пуст `total`. */
  totalNet: string | null;
  /** Действующие условия и то, на основании чего каждое из них таково. */
  terms: ContractTermsView;
  /**
   * Свой порядковый номер допсоглашения внутри рамочного договора: нигде не
   * хранится, считается сервером как место в ряду детей. У рамочного и у
   * разового договора ноль.
   */
  amendNo: number;
  /** Тот же номер словами, со ссылкой на номер рамочного договора. */
  amendLabel: string;
  limit: { amount: string; used: string; rest: string } | null;
  currency: string;
  vatRate: number;
  /** Налог внутри суммы: при нулевой ставке ноль. */
  vat: string;
  taxMode: string;
  taxModeAt: string | null;
  /** Срок действия: дата, до которой договор действует. Одна на все виды. */
  validTo: string | null;
  payments: ContractPaymentView[];
  paymentsRest: string;
  amount: string;
  vatIncluded: boolean;
  form: string;
  signing: string;
  status: ContractStatus;
  statusLabel: string;
  round: number;
  initiator: { login: string; name: string };
  holder: Doer | null;
  visas: { kind: 'legal' | 'finance' | 'accounting'; title: string; doer: Doer | null }[];
  sign: Doer | null;
  scan: Doer | null;
  number: string;
  /**
   * Номер на шаге «Выбрать способ подписания и присвоить номер»: как он
   * присвоится, что мешает (`note` — тот же текст, что отказ сервера) и
   * каким допсоглашениям пакета вписать номер руками. Пусто — шага нет или
   * номер уже есть.
   */
  numbering: { mode: NumberingMode; note: string; package: { id: string; title: string }[] } | null;
  date: string | null;
  cancelReason: string;
  signedAt: string | null;
  guest: {
    open: boolean;
    openedAt: string | null;
    /**
     * Только гости контрагента (гости УК — в `uk.guest`) и только вошедшие
     * по прежней общей ссылке: вошедшие по приглашению — в `invites`.
     */
    guests: { id: string; name: string; position: string; phone: string; joinedAt: string; removed: boolean }[];
    invites: ContractInviteRow[];
  };
  /**
   * УК в согласовании договора фонда (часть 4); пусто — УК не участвует
   * (сторона-организация или договор, заведённый до части 4).
   */
  uk: {
    mode: UkModeName;
    /** УК нашей стороны-фонда. */
    manager: string;
    /** Ответы по кругам, новые сверху. */
    replies: {
      id: string;
      round: number;
      remarks: boolean;
      /** Файл замечаний; пусто — «без замечаний» или ответ по ссылке. */
      fileName: string;
      /** Имя сотрудника или «<гость> (УК)». */
      by: string;
      createdAt: string;
    }[];
    /** Есть ли ответ по текущему кругу. */
    replied: boolean;
    /** Последний скан, подписанный УК. */
    signed: { id: string; name: string; createdAt: string } | null;
    /** Приглашённые от УК; только в режиме «внутри». */
    guest: {
      open: boolean;
      openedAt: string | null;
      guests: { id: string; name: string; position: string; phone: string; joinedAt: string; removed: boolean }[];
      invites: ContractInviteRow[];
    } | null;
  } | null;
  /** Места файлов версии по порядку: договор, приложение, смета. */
  places: { id: string; title: string }[];
  versions: {
    id: string;
    /** Место файла; у подписанного скана пусто — он вне круга версий. */
    placeId: string;
    round: number;
    name: string;
    size: number;
    fromContractor: boolean;
    frozen: boolean;
    signed: boolean;
    uploadedBy: string;
    createdAt: string;
    hasPdf: boolean;
  }[];
  /** Собираемый набор следующего круга; `null` — набора нет. */
  nextVersion: {
    files: { id: string; placeId: string; name: string; size: number; warn: string }[];
    /** Чего не хватает набору, словами. Пусто — можно отправлять. */
    gap: string;
  } | null;
  events: {
    id: string;
    kind: string;
    text: string;
    actor: string;
    /** Имя и должность автора: история у договора та же, что у подрядчика. */
    actorName: string;
    actorPosition: string;
    reason: string;
    fromStatus: string;
    toStatus: string;
    createdAt: string;
  }[];
  roles: ContractRole[];
  /** Право писать комментарий: та же проверка, что и на сервере (`assertCommenter`). */
  canComment: boolean;
  actions: ContractAction[];
  /** Требования статуса в порядке хода: ими собран раздел «Доступные действия». */
  needs: ContractNeedRow[];
  daysInStatus: number;
};

/** Строка реестра. Валюта идёт вместе с суммой: без неё любая читалась бы рублём. */
export type ContractRow = Pick<
  ContractCardView,
  | 'id'
  | 'seq'
  | 'subject'
  | 'status'
  | 'statusLabel'
  | 'round'
  | 'amount'
  | 'currency'
  | 'kindLabel'
  | 'framework'
  | 'amendLabel'
  | 'daysInStatus'
  | 'number'
  | 'parentId'
  | 'amendsId'
  | 'amendKind'
  | 'party'
  | 'purpose'
  | 'contractType'
  | 'intraGroup'
  | 'vatIncluded'
  | 'vatRate'
> & {
  project: { id: string; code: string; name: string } | null;
  holderName: string;
  /** Наше юрлицо коротким именем. */
  legalName: string;
  /** Наше юрлицо: по нему фильтрует реестр. */
  legalEntityId: string;
};

/** Строка платёжного календаря так, как её набирают на форме заведения. */
/** `regular` — строка из действия «Регулярный платёж»: в реестр приходит без заявки (спека 2026-10-07). */
export type ContractPaymentInput = { amount: string; date: string; kind: PaymentKind; regular?: boolean };

/**
 * Реквизиты заводимого договора: уходят строкой запроса, потому что тело
 * занято файлом. Пустая строка значит «не указано» — так же читает их сервер
 * (server/src/contracts/contracts.service.ts).
 */
export type ContractInput = {
  /** Контрагент из реестра — сторона договора. */
  counterpartyId?: string;
  /** Где наша сторона у типа с направлением: `PAYER` или `PERFORMER`; у типа без направления пусто. */
  ourSide?: 'PAYER' | 'PERFORMER' | '';
  /** Тип; у закупки по виду работ пусто значит тип направления предмета. */
  type: ContractType | '';
  /** Предмет текстом, когда он не вид работ. */
  subjectText: string;
  /** Договор семьи: указан — заводится допсоглашение к нему. */
  parentId?: string;
  /**
   * Род допсоглашения: объявляется формой, а не выводится сервером из
   * глубины связи. Пусто у самостоятельного договора.
   */
  amendKind?: AmendKind | '';
  /** Что изменяет ДС об изменении; пусто у ДС на новый предмет и у самостоятельного договора. */
  amendsId?: string;
  /**
   * Какие условия ДС об изменении объявляет изменёнными: имена из
   * `AMENDABLE`. Необъявленное оно несёт прежним — его подставляет сервер, и
   * слать его формой не надо.
   */
  amendChanges?: string[];
  legalEntityId: string;
  projectId: string;
  /** Предмет: узел классификатора работ; у предмета текстом пусто. */
  categoryId: string;
  /** Закрывающие: набор из допустимых для типа; у рамочного и ДС об изменении пусто. */
  closing: ClosingSet | '';
  framework: boolean;
  currency: string;
  /**
   * Налоги. У ДС на новый предмет и его изменений не уходят вовсе: их задаёт
   * договор, и сервер берёт их у него (решение Ильи 25.09.2026).
   */
  vatRate?: number;
  taxMode?: string;
  taxModeAt?: string;
  amount: string;
  validTo: string;
  form: 'OURS' | 'THEIRS';
  payments: ContractPaymentInput[];
  /**
   * Допсоглашения, заведённые вместе с договором: согласуются его пакетом.
   * У рамочного — новые объёмы со своими условиями, у разового — без своих условий.
   */
  package?: {
    categoryId: string;
    amount: string;
    validTo: string;
    payments: ContractPaymentInput[];
    closing: ClosingSet | '';
  }[];
  /**
   * Названия остальных частей договора: первую занимает файл этого же
   * запроса, их файлы едут следом. Объявляются сразу, потому что круг
   * открывается заполненным набором, и сервер обязан знать, чего ещё ждёт.
   */
  titles?: string[];
  /** Режим УК у стороны-фонда: `INSIDE` («по ссылке в системе») или `OUTSIDE`. */
  ukMode?: string;
};

/*
 * Замечания к договору и сравнение версий. Типы — копия видов сервера
 * (server/src/contracts/comments.service.ts и anchors.ts) с приставкой
 * Contract: имя CommentView здесь уже занято комментариями к регламенту.
 */

export type CommentVisibility = 'INTERNAL' | 'SHARED' | 'UK';
export type CommentResolution = 'NONE' | 'ACCEPTED' | 'REJECTED';

export type ContractCommentView = {
  id: string;
  parentId: string | null;
  versionId: string;
  /** Место, к документу которого относится ветка: лента идёт по местам. */
  placeId: string;
  paragraph: number;
  label: string;
  quote: string;
  /**
   * Логин автора-сотрудника нужен только своей стороне: по гостевой ссылке
   * сервер его не отдаёт вовсе (server/src/contracts/guest.service.ts).
   */
  author: { login?: string; name: string; title: string } | { guest: string; company: string };
  text: string;
  proposal: string;
  visibility: CommentVisibility;
  resolution: CommentResolution;
  /**
   * Пояснение держателя к «отклонено» — запись для своих: по гостевой ссылке
   * сервер его не отдаёт вовсе (server/src/contracts/guest.service.ts).
   */
  resolutionNote?: string;
  /** MOVED — цитата не нашлась в новой версии, место решает держатель. */
  anchorState: 'OK' | 'MOVED';
  createdAt: string;
  replies: ContractCommentView[];
};

export type ContractCommentInput = {
  versionId: string;
  paragraph: number;
  quote: string;
  offset?: number;
  text: string;
  proposal?: string;
  visibility?: CommentVisibility;
  parentId?: string | null;
};

/** Разбор двух версий по слепку абзацев: строка сравнения и слова внутри неё. */
export type ContractParagraph = { i: number; label: string; text: string };
export type ContractDiffWord = { kind: 'same' | 'ins' | 'del'; text: string };
export type ContractDiffRow = {
  kind: 'same' | 'added' | 'removed' | 'changed';
  a?: ContractParagraph;
  b?: ContractParagraph;
  words?: ContractDiffWord[];
};

/** У версий старше преобразователя слепка нет: сравнивать тогда нечего. */
export type ContractCompareView = { ready: boolean; rows: ContractDiffRow[] };

/*
 * Договор по гостевой ссылке (server/src/contracts/guest.service.ts). Это
 * другая сторона стола, поэтому и вид другой: ни суммы, ни проекта, ни
 * заданий здесь нет вовсе — сервер их не отдаёт.
 */

export type GuestContractView = {
  /** Сторона договора по имени: контрагент, у старых — организация подрядчика. */
  party: string;
  legalEntity: string;
  subject: string;
  round: number;
  status: ContractStatus;
  guest: { name: string; position: string } | null;
  /** Чья это ссылка (часть 4): контрагента или представителя УК. */
  side: 'PARTY' | 'UK';
  /** Ответ УК по текущему кругу (последний); у ссылки контрагента пусто. */
  ukAnswer: 'OK' | 'REMARKS' | null;
};

/** Версия круга глазами гостя: размера и «от подрядчика» здесь нет. */
export type GuestContractVersion = {
  id: string;
  /** Место и его название: подрядчик видит те же места, что и мы. */
  placeId: string;
  placeTitle: string;
  round: number;
  name: string;
  createdAt: string;
  hasPdf: boolean;
};

/** Состояние ссылки из письма; почта названа наполовину, у мёртвой пуста. */
export type GuestPassState = { state: 'LIVE' | 'USED' | 'EXPIRED' | 'DEAD'; email: string };

/** Производственный календарь на год: по символу на день, 1 — нерабочий. */
export type CalendarYearView = {
  year: number;
  days: string;
  source: string;
  updatedAt: string | null;
  offDays: number;
  /** `LOADED` — год из службы с переносами, `TEMPORARY` — свой расчёт без них. */
  state: 'LOADED' | 'TEMPORARY';
};

/** Критерий в настройке: то, что правит администратор. */
export type CriterionView = {
  id: string;
  code: string;
  kind: CheckKind;
  directionId: string | null;
  direction: { id: string; code: string; name: string } | null;
  title: string;
  green: string;
  yellow: string;
  red: string;
  weight: number;
  stop: boolean;
  auto: boolean;
  hint: string;
  source: string;
  order: number;
  active: boolean;
  builtin: boolean;
};

/** Задание: очередь по должности или взятое в работу. */
export type AssignmentView = {
  id: string;
  typeCode: string;
  typeName: string;
  targetType: string;
  targetId: string;
  positionId: string | null;
  positionName: string;
  userId: string | null;
  userName: string;
  state: 'WAITING' | 'TAKEN' | 'DONE' | 'CANCELLED';
  createdAt: string;
  takenAt: string | null;
  dueAt: string | null;
  overdue: boolean;
  /** Срок словами по производственному календарю: «осталось 3 рабочих дня». */
  dueIn: string;
  workDaysLeft: number | null;
  /** Что сделать: «Проверить подрядчика». */
  action: string;
  /** По какому объекту: «ООО «СтройФасад»». */
  targetName: string;
  /** Раздел, в котором живёт работа: «Реестр подрядчиков». */
  sectionName: string;
  /** Сколько людей видит задание в очереди, включая получателя. */
  queueSize: number;
  /** Проверка, которую открывает задание. */
  checkId: string | null;
  title: string;
};

/** Отчёт по соблюдению сроков: по людям и по процессам. */
export type SlaReport = {
  people: {
    userId: string;
    name: string;
    positionName: string;
    done: number;
    inTime: number;
    breached: number;
    share: number;
    avgHours: number;
    overdueNow: number;
  }[];
  processes: {
    code: string;
    name: string;
    slaLabel: string;
    done: number;
    inTime: number;
    breached: number;
    share: number;
    avgHours: number;
    avgTakeHours: number;
  }[];
};

/** Предпросмотр реквизитов по ИНН на экране заведения. */
export type PartyLookup = {
  party: {
    name: string;
    fullName: string;
    legalForm: LegalForm;
    /** Форма словами, как её пишет реестр. */
    legalFormName: string;
    inn: string;
    ogrn: string;
    kpp: string;
    address: string;
    director: string;
    okved: string;
    status: string;
    registeredAt: string | null;
  } | null;
  alreadyId: string | null;
  alreadyName: string;
  /** Досье начато, но не отправлено: такую карточку продолжают, а не заводят. */
  draft: boolean;
  enabled: boolean;
};

/** Запись классификатора работ: направление (parentId пуст) или вид работ. */
export type WorkCategory = {
  id: string;
  code: string;
  name: string;
  parentId: string | null;
  hidden: boolean;
  packages: number;
  contractors: number;
  /** Тип договора: свой у направления, унаследованный у вида работ. */
  contractType: ContractType | null;
  /** Функция группы, чей это стандартный вид работ; у прочих пусто (спека 2026-09-30). */
  function?: FunctionName | null;
};

// ---------- контрагенты ----------

/** Вид организации (копия server/src/counterparties/fund.model.ts). Пусто — организация. */
export const ORG_FORMS = ['ORG', 'FUND'] as const;
export type OrgFormName = (typeof ORG_FORMS)[number];

export const UK_MODES = ['INSIDE', 'OUTSIDE'] as const;
export type UkModeName = (typeof UK_MODES)[number];

/** Вид документа фонда (копия server/src/counterparties/fund.model.ts). Пусто — правила ДУ. */
export const FUND_DOC_KINDS = ['RULES', 'CBR_NOTICE', 'UK_LICENSE', 'SPECDEP'] as const;
export type FundDocKindName = (typeof FUND_DOC_KINDS)[number];

export type FundFileView = {
  id: string; name: string; size: number; regNo: string;
  registeredAt: string | null; addedBy: string; createdAt: string; kind: FundDocKindName;
};

export type FundContactView = { id: string; name: string; position: string; email: string; phone: string; isPrimary: boolean };

export type FundProjectView = { id: string; name: string };

/** Карточка фонда: документы, контакты и проекты (копия FundView сервера). Реквизиты УК не заводим — общее правило для всех контрагентов. */
export type FundView = {
  orgId: string;
  name: string;
  title: string;
  partyName: string;
  manager: { orgId: string; name: string; inn: string; group: boolean };
  files: FundFileView[];
  contacts: FundContactView[];
  /** У нашего фонда — проекты, где он заказчик; у чужого — пусто. */
  projects: FundProjectView[];
  legalEntityId: string | null;
  contracts: number;
};

export type FundInput = {
  name?: string;
  manager?: { orgId?: string; inn?: string; name?: string };
  /** Только у чужого фонда. */
  roles?: unknown;
  /** Только при заведении нашего фонда: код в номере договора. */
  code?: string;
};

export type ManagerOption = { orgId: string; name: string; inn: string; group: boolean };

/** Роли контрагента (копия server/src/counterparties/counterparties.model.ts). */
export const OFFERED_ROLES = ['CONTRACTOR', 'CLIENT', 'BANK', 'INSURER', 'PARTNER'] as const;
/** Прежние роли: не предлагаются при заведении и правке, но читаются как есть. */
export const LEGACY_ROLES = ['INVESTOR', 'OTHER'] as const;
export const STORED_ROLES = [...OFFERED_ROLES, ...LEGACY_ROLES] as const;
export type StoredRole = (typeof STORED_ROLES)[number];
export type CounterpartyRole = StoredRole | 'GROUP' | 'GROUP_FUND';

export type CounterpartyRow = {
  orgId: string;
  name: string;
  form: OrgFormName;
  /** УК фонда. */
  manager: { orgId: string; name: string } | null;
  /** Как организация называется стороной договора. */
  partyName: string;
  inn: string;
  roles: CounterpartyRole[];
  profileId: string | null;
  legalEntityId: string | null;
  contractorStatus: ContractorStatus | null;
  /** Бумажная часть: у компании — своя, у чужого фонда — его УК, у наших — 'ALLOWED'. */
  paper: 'WAITING' | 'ALLOWED' | 'BLOCKED';
  /** Чёрный список или приостановка (у фонда — его УК). */
  closed: boolean;
};

export type CounterpartyCardView = CounterpartyRow & {
  kpp: string; ogrn: string; stored: StoredRole[];
  /** Карточка фонда, без байтов правил. */
  fund: FundView | null;
  /** Фонды, которыми организация управляет как УК. */
  managed: { orgId: string; name: string }[];
  /** Код в номере договора нашего фонда; у чужого и у нашего без кода — пусто. */
  legalCode: string;
};

export type CounterpartyLookup = {
  party: PartyLookup['party'];
  taken: { orgId: string; name: string; listed: boolean } | null;
  enabled: boolean;
};

/** Юрлицо, от чьего имени подписывается договор. Реквизиты — только в LegalEntityView. */
export type LegalEntityInput = {
  name: string;
  fullName: string;
  inn?: string;
  kpp?: string;
  ogrn?: string;
  address?: string;
  bankName?: string;
  bik?: string;
  account?: string;
  corrAccount?: string;
  /** Код в номере договора по нашей форме: пять заглавных кириллических букв. */
  code?: string;
};

/** Шаг с ИНН при заведении юрлица: что нашёл справочник и не занят ли номер. */
export type LegalLookup = {
  party: PartyLookup['party'];
  taken: { name: string; kind: 'legal' | 'contractor' } | null;
  enabled: boolean;
};

export type LegalEntityView = LegalEntityInput & {
  id: string;
  orgId: string;
  name: string;
  inn: string;
  kpp: string;
  ogrn: string;
  code: string;
  /** Код закреплён: по юрлицу выдан хоть один номер. */
  issued: boolean;
  projects: number;
  contracts: number;
  form: OrgFormName;
  fund: FundView | null;
};

/** Заведённое юрлицо: `profileId` — его карточка компании, куда мастер идёт за досье и контактами. */
export type LegalCreated = LegalEntityView & { profileId: string };

export type ProjectView = {
  id: string;
  code: string;
  name: string;
  address: string;
  status: ProjectStatus;
  areaM2: number | null;
  budget: number | null;
  lat: number | null;
  lon: number | null;
  startPlan: string | null;
  finishPlan: string | null;
  description: string;
  createdBy: string;
  sprintStartDay: number; // 1 = пн … 7 = вс
  createdAt: string;
  /** Функция работы; прежние проекты — генподряд (спека 2026-09-30). */
  fn: FunctionName;
  /** Здание работы; точка карты — его, а не копия у работы. */
  building: { id: string; name: string; address: string; lat: number | null; lon: number | null } | null;
  /** Исполнитель — наша компания; у работы «Собственник» — сам собственник. */
  performer: { id: string; name: string } | null;
  /** Собственник здания: исполнитель его открытой работы «Собственник». */
  owner: { id: string; name: string } | null;
  baseContract: BaseContractBrief | null;
  /** Состояние выводится сервером: закрыта, по договору, договор готовится, силами собственника, без договора. */
  state: WorkState;
  closedAt: string | null;
  closeReason: string;
};

/** Договор-основание работы коротко (спека 2026-09-30); предмет называет его, пока номера нет. */
export type BaseContractBrief = { id: string; number: string; status: ContractStatus; subject: string };

/** Вид фазы здания (копия BuildingPhaseKind из prisma/schema.prisma). */
export type BuildingPhaseKind = 'PURCHASE' | 'BUILD' | 'OPERATE' | 'SALE';

/** Фаза здания: отметка с датами, фазы идут внахлёст; без даты окончания — идёт. */
export type BuildingPhaseView = { id: string; kind: BuildingPhaseKind; startedOn: string; endedOn: string | null };

/** Работа в списке вкладки «Функций» (GET /projects?fn=). */
export type WorkListItem = {
  id: string;
  code: string;
  name: string;
  fn: FunctionName;
  building: { id: string; name: string; address: string } | null;
  performer: { id: string; name: string } | null;
  state: WorkState;
  closedAt: string | null;
  closeReason: string;
  baseContract: BaseContractBrief | null;
  /** Только у работы «Собственник»: фазы здания и его открытые работы функций для чипов строки. */
  phases?: BuildingPhaseView[];
  functions?: { fn: FunctionName; state: WorkState }[];
};

/** Работа функции на здании — строка блока «Типовые договоры». */
export type WorkBrief = {
  id: string;
  code: string;
  performer: { id: string; name: string } | null;
  state: WorkState;
  baseContract: BaseContractBrief | null;
  closedAt: string | null;
  closeReason: string;
};

/** Здание со стороны его работы «Собственник» (GET /projects/:id/building). */
export type BuildingView = {
  id: string;
  name: string;
  address: string;
  lat: number | null;
  lon: number | null;
  closedAt: string | null;
  closeReason: string;
  phases: BuildingPhaseView[];
  owner: { id: string; name: string } | null;
  /** Всегда четыре строки: генпроектировщик, генподрядчик, брокер, управление. */
  functions: { fn: Exclude<FunctionName, 'OWNER'>; works: WorkBrief[] }[];
  /** Виды документов досье здания, двенадцать (код и подпись). */
  docs: { kind: string; label: string }[];
  /** Файлы досье: по одному на вид документа. */
  files: { id: string; kind: string; name: string; createdAt: string }[];
};

/** Место в команде при заведении здания. */
export type TeamSeat = { userId: string; positionId: string };

/** Плитка собственника вкладки «Собственник» (GET /owners): `buildings` — открытые здания, видимые человеку. */
export type OwnerTile = { id: string; legalEntityId: string; orgId: string; name: string; form: 'ORG' | 'FUND'; buildings: number };

/** Плитки собственников и работы «Собственник» без исполнителя — «Здания без собственника». */
export type OwnersView = { owners: OwnerTile[]; orphans: WorkListItem[] };

/** Кем может стать собственник: наше юрлицо или фонд, ещё не собственник (GET /owners/candidates); `id` — юрлицо. */
export type OwnerCandidate = { id: string; orgId: string; name: string; form: 'ORG' | 'FUND' };

/** Карточка собственника (GET /owners/:id): здания — работы «Собственник» его юрлица, закрытые тоже. */
export type OwnerCardView = Omit<OwnerTile, 'buildings'> & { buildings: WorkListItem[] };

/** Досье юрлица собственника: у компании — виды документов и файлы её карточки, у фонда — его файлы. */
export type OwnerDossier =
  | { form: 'ORG'; docs: { kind: string; label: string }[]; files: { id: string; kind: string; name: string }[] }
  | { form: 'FUND'; files: { id: string; kind: string | null; name: string }[] };

/** Исполнитель функции: `id` — юрлицо (для «Открыть»), `orgId` — организация (вторая сторона договора). */
export type PerformerOption = { id: string; orgId: string; name: string };

/**
 * Работа для поля «Работа» формы договора (GET /contracts/works): открытые,
 * куда человек номинирован. Подпись — `workLabel`.
 */
export type WorkOption = {
  id: string;
  code: string;
  fn: FunctionName;
  building: { id: string; name: string } | null;
  performer: { id: string; name: string } | null;
  /** Наши стороны договора на этой работе: ограничивают «нашу сторону». */
  sides: string[];
  /** Фонд-заказчик старого проекта с ЗПИФ: аренда и продажа помещений — от него (решение 19). */
  fundId: string | null;
};

export type TeamMember = {
  id: string;
  userId: string;
  login: string;
  name: string;
  positionId: string;
  positionCode: string;
  positionName: string;
  startsAt: string | null;
  endsAt: string | null;
  substitutesFor: string | null;
  active: boolean;
};

export type SprintTaskView = {
  id: string;
  title: string;
  description: string;
  assignee: string;
  done: boolean;
  doneAt: string | null;
  carriedFrom: string | null;
  carriedFromEnd: string | null;
  carriedTo: boolean;
  columnId: string | null;
  attrs: Record<string, unknown>;
};

export type SprintColumnView = {
  id: string;
  name: string;
  tasks: SprintTaskView[];
};

export type SprintListItem = {
  id: string | null;
  startDate: string;
  endDate: string;
  index: number; // №1 — спринт недели создания проекта
};

export type SprintBoard = {
  id: string | null;
  startDate: string;
  endDate: string;
  columns: SprintColumnView[];
  tasks: SprintTaskView[];
};

export type SprintOverview = {
  sprintStartDay: number;
  today: string;
  all: SprintListItem[];
  prev: {
    id: string | null;
    startDate: string;
    endDate: string;
    columns: SprintColumnView[];
    tasks: SprintTaskView[];
  };
  current: {
    id: string;
    startDate: string;
    endDate: string;
    dayIndex: number;
    totalDays: number;
    planningDay: boolean;
    columns: SprintColumnView[];
    tasks: SprintTaskView[];
  };
  next: { startDate: string; endDate: string };
};

export type TaskFeedItem = {
  id: number;
  kind: 'comment' | 'event';
  login: string;
  name: string;
  admin?: boolean;
  text: string;
  replyTo: number | null;
  edited: boolean;
  fileId: number | null;
  fileName: string | null;
  reactions: Record<string, { login: string; name: string }[]>;
  ts: number;
};

export type TaskFileItem = {
  id: number;
  name: string;
  mime: string;
  size: number;
  uploadedBy: string;
  ts: number;
};

export type PositionView = {
  id: string;
  code: string;
  name: string;
  sub: string;
  parentCode: string | null;
  level: number;
  orderIdx: number;
  /** Сколько человек сейчас на должности. */
  people: number;
};

export type UserPositionView = {
  id?: string;
  positionId: string;
  code: string;
  name: string;
  isPrimary: boolean;
  startsAt: string | null;
  endsAt: string | null;
  active?: boolean;
};

export type PositionInput = {
  positionId: string;
  isPrimary?: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
};

/*
 * Доступы: дерево разделов, действий и процессов (server/src/access/registry.ts,
 * server/src/access/grants.model.ts). Клиент не импортирует серверный код,
 * поэтому типы повторены здесь по тому же образцу. Спека:
 * docs/superpowers/specs/2026-09-13-access-tree-design.md.
 */
export type AccessMobileLevel = 'none' | 'full';

export type Block = {
  kind: 'block';
  code: string;
  name: string;
  purpose: string;
  /** Убрать нельзя: без этого приложением не пользуются. */
  locked: boolean;
  mobile: AccessMobileLevel;
};

export type Section = {
  kind: 'section';
  code: string;
  name: string;
  purpose: string;
  /** Раздел, внутри которого живёт этот; пусто — первый уровень. */
  parent?: string;
  /** Виден в тех работах, куда человек номинирован; охват «видит все» стоит
      на разделах функций `registry.reach`, у прочих сужённых своего нет. */
  scoped?: boolean;
  mobile: AccessMobileLevel;
};

export type Stage = {
  code: string;
  name: string;
  start: 'manual' | 'assigned';
  changes: string;
  slaDays?: number;
  /** Роль процесса, которая выполняет этап; только у назначаемых. */
  role?: string;
  what: string;
};

/** Роль процесса: должность назначается на роль, а не на её этапы. */
export type Role = {
  code: string;
  name: string;
  /** Что роль делает: единственное описание на экране «Доступы». */
  what: string;
};

export type Process = {
  kind: 'process';
  code: string;
  name: string;
  purpose: string;
  /** Раздел, внутри которого идёт работа. */
  parent: string;
  mobile: AccessMobileLevel;
  stages: Stage[];
  /** Роли процесса: должность назначается на роль, этапы роли её получают. */
  roles?: Role[];
};

/** `assignee` — исполнитель этапа; `on` — блок целиком; `view` — раздел. Уровня «вести» нет. */
export type GrantLevel = 'on' | 'view' | 'assignee';
export type AccessScope = 'own' | 'all';

export type AccessGrant = { positionId: string; code: string; level: GrantLevel; scope: AccessScope };

export type AccessIssue = {
  code: string;
  /** Должность, о которой речь; у находки «этап без исполнителя» её нет. */
  positionId?: string;
  kind: 'stage-without-assignee';
  text: string;
};

export type AccessOverview = {
  /** Разделы функций, на которых стоит охват «видит все» (server/src/access/registry.ts,
      REACH_CODES). Прочие сужённые разделы своего охвата не имеют. */
  registry: { blocks: Block[]; sections: Section[]; processes: Process[]; reach: string[] };
  positions: { id: string; code: string; name: string }[];
  grants: AccessGrant[];
  /** Коды, открытые выводом, а не назначением: должность → коды. */
  implied: Record<string, string[]>;
  mobile: Record<string, AccessMobileLevel>;
  stages: Record<string, { slaDays: number | null; workTimeOnly: boolean }>;
  issues: AccessIssue[];
};

export type InvitePreview = {
  firstName: string;
  lastName: string;
  email: string | null;
  position: string;
};

export type AdminUser = {
  id: string;
  login: string;
  email: string | null;
  /** Приглашение ещё не принято: человек не задал пароль. */
  invited: boolean;
  inviteExpired: boolean;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  org: string;
  positions: UserPositionView[];
  createdAt: string;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

/*
 * Истёкшая сессия. 401 может прийти в любой запрос, и реакция на него не
 * должна зависеть от того, поймал ли отказ экран своим `catch`. Раньше App
 * слушал `unhandledrejection` — то есть только необработанные ошибки, — и
 * лента чата, у которой свой `catch`, этот путь себе отключала: человек
 * оставался в приложении, где не работает ни один запрос, и смотрел на
 * баннер с английским «Unauthorized».
 *
 * Пути входа исключены: там 401 значит «неверный логин или пароль»
 * (server/src/auth/auth.service.ts), а не конец сессии.
 *
 * Сессию закрыл сервер (отключили, сменили пароль), пока вкладка спала и
 * события `session` не слышала, — причина приходит в теле отказа
 * (server/src/auth/auth.guard.ts, `sessionRefusal`). Нет её — просто истекла.
 */
export type SessionEndReason = 'disabled' | 'password';

let sessionExpired: ((reason?: SessionEndReason) => void) | null = null;

export function onSessionExpired(cb: ((reason?: SessionEndReason) => void) | null): void {
  sessionExpired = cb;
}

const endReasonOf = (data: unknown): SessionEndReason | undefined => {
  const reason = (data as { reason?: unknown } | null)?.reason;
  return reason === 'disabled' || reason === 'password' ? reason : undefined;
};

const LOGIN_PATHS = ['/auth/login', '/auth/pin-login'];

/**
 * GET с повтором: сетевой сбой или 5xx (сервер перезапускается при деплое)
 * не должны оставлять пользователя с пустым экраном. 4xx не повторяются.
 */

// ---------- чат ----------

export type ChatPerson = {
  id: string;
  login: string;
  name: string;
  avatarAt: string | null;
  /** Когда человек был активен, секунды эпохи. */
  lastSeen: number | null;
  /** Служебная запись (помощник Педро), а не сотрудник. */
  service?: boolean;
};

/* `system` — чат уведомлений: он не лежит в базе, а собирается из списка
   уведомлений (см. NOTIFY_CHANNEL_ID в ChatProvider). Писать в него нельзя. */
export type ChatKind = 'group' | 'channel' | 'dm' | 'system';

export type ChatChannel = {
  id: string;
  kind: ChatKind;
  name: string;
  topic: string;
  isPrivate: boolean;
  projectId: string | null;
  archived: boolean;
  peer: ChatPerson | null;
  peerOnline: boolean;
  /** Собеседник работает; ложь — личка с уволенным только для чтения. */
  peerActive: boolean;
  /** Лента и ответы в моих ветках; чужие ветки число не меняют. */
  unread: number;
  /** Первое непрочитанное сообщение: перед ним в ленте встаёт отсечка «новые». */
  firstUnreadId: number | null;
  /** Ветка с самым ранним непрочитанным ответом: туда ведёт стрелка, когда лента дочитана. */
  threadRootId: number | null;
  /** Непрочитанные обращения ко мне «@логин» и первое из них; `rootId` — корень ветки. */
  mentions: number;
  mention: { id: number; rootId: number | null } | null;
  /** Закреплённое сообщение: одно на чат, висит в шапке, пока не открепят. */
  pinned: { id: number; author: string; text: string } | null;
  muted: boolean;
  role: string;
  /** Может ли этот человек писать сюда: в канале — только администраторы. */
  canPost: boolean;
  avatarAt: string | null;
  lastMessageAt: number;
  /** Убран из списка своей рукой: вернётся с новым сообщением. */
  hidden: boolean;
  /** Своя папка, если чат в неё положили. */
  folderId: string | null;
  last: ChatLastMessage | null;
};

/** Своя папка чатов: у каждого сотрудника свой набор. */
export type ChatFolder = { id: string; name: string; orderIdx: number };

/** Чем закончился чат: список рисует иконку перед текстом превью. */
export type ChatPreviewKind = 'text' | 'call' | 'video-call' | 'voice' | 'image' | 'video' | 'file' | 'files';

export type ChatLastMessage = {
  id: number;
  text: string;
  /** Тип последнего сообщения; у старых ответов сервера его может не быть. */
  kind?: ChatPreviewKind;
  authorName: string;
  mine: boolean;
  ts: number;
  seenByAll: boolean;
};

export type ChatProfile = {
  id: string;
  kind: ChatKind;
  name: string;
  topic: string;
  isPrivate: boolean;
  projectId: string | null;
  avatarAt: string | null;
  createdAt: number;
  myRole: string;
  canEdit: boolean;
  canEditMembers: boolean;
  members: (ChatPerson & { role: string })[];
};

export type ChatPersonCard = ChatPerson & {
  firstName: string;
  lastName: string;
  email: string | null;
  birthday: string | null;
  isAdmin: boolean;
  active: boolean;
  online: boolean;
  positions: { name: string; code: string; sub: string; isPrimary: boolean }[];
  dmChannelId: string | null;
};

export type ChatCallParticipant = {
  /** Тот же идентификатор, что у участника комнаты: у гостя `guest:<строка>`. */
  id: string;
  kind: 'user' | 'guest';
  name: string;
  login: string;
  avatarAt: string | null;
  lastSeen: number | null;
  status: 'ringing' | 'active' | 'left' | 'declined';
};

/** Вложение сообщения звонка. */
/**
 * Загрузка файла сырым телом: multipart в проекте не используется, имя и тип
 * идут в адресе (тот же приём, что у подложки листа доски и файлов обхода).
 */
function rawUpload<T>(url: string, file: File, onProgress?: (fraction: number) => void): Promise<T> {
  const qs = new URLSearchParams({ name: file.name, mime: file.type || 'application/octet-stream' });
  // У адреса могут быть свои параметры: приклеивать второй «?» нельзя, иначе
  // последний чужой параметр съедает всю нашу строку (из-за этого срок
  // действия документа приезжал на сервер как «?name=…» и ронял запись).
  const at = `${url}${url.includes('?') ? '&' : '?'}${qs}`;
  /*
   * XMLHttpRequest, а не fetch: о ходе отправки fetch молчит, и на договорном
   * docx это выглядело молчанием на несколько секунд. Тот же приём и по тем
   * же причинам стоит у вложений чата (`chatUpload`).
   */
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', at);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      let data: unknown = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* сервер ответил не JSON — сообщение возьмём по умолчанию */
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve(data as T);
        return;
      }
      reject(new ApiError(xhr.status, (data as { message?: string } | null)?.message ?? 'Не удалось загрузить файл'));
    };
    xhr.onerror = () => reject(new ApiError(0, 'Сеть недоступна'));
    xhr.onabort = () => reject(new ApiError(0, 'Отправка прервана'));
    xhr.send(file);
  });
}

export type CallFile = { id: number; name: string; mime: string; size: number };

/** Сообщение переписки звонка: автор — участник, а не учётная запись. */
export type CallMessage = {
  id: number;
  author: { id: string; kind: 'user' | 'guest'; name: string };
  text: string;
  files: CallFile[];
  /** Время отправки, ISO. */
  at: string;
};

export type ChatCall = {
  id: string;
  kind: 'audio' | 'video';
  status: string;
  startedAt: number;
  /** Кто начал звонок: только он приглашает новых людей. */
  adminId: string;
  presenterId: string;
  /** Ведущий: только он распоряжается листами доски (открыть/загрузить/закрыть). Всегда сотрудник. */
  boardHostId: string;
  /** Держатель права показа экрана — доски не касается, может быть гостем. */
  screenShareHolderId: string;
  /** Канал звонка команды; пусто — звонок из чата или карточки сотрудника. */
  channelId: string | null;
  /** Код ссылки комнаты; пусто — звонок без комнаты. */
  linkToken: string | null;
  isRoom: boolean;
  /** Комната встречи календаря: звать людей в неё можно только через карточку встречи. */
  meeting: boolean;
  /** До какого времени идёт отсчёт в шапке (ISO); пусто — отсчёта нет. */
  timerEndsAt: string | null;
  participants: ChatCallParticipant[];
  /** Поля этапов 1–2, пока живы вкладки, открытые до групповых звонков. */
  startedBy: ChatPerson;
  peers: ChatPerson[];
  startedById: string;
};

// ---------- доска в звонке ----------

export type SheetKind = 'blank' | 'page';

export type SheetStroke = {
  /** Идентификатор штриха в базе — нужен ластику, чтобы стереть конкретный штрих. */
  id: number;
  /** Автор в формате partId (сотрудник — id, гость — `guest:<id>`); нужен только для отмены и ластика. */
  author: string;
  color: string;
  width: number;
  pts: { x: number; y: number }[];
};

export type SheetView = {
  id: string;
  callId: string;
  kind: SheetKind;
  sourceName: string;
  sourcePage: number | null;
  /** Адрес подложки (не сама картинка) — GET отдельным запросом; пусто у чистого листа. */
  background: string | null;
  width: number;
  height: number;
  strokes: SheetStroke[];
  openedById: string;
  openedAt: number;
  closedAt: number | null;
  /** Адрес итогового файла; непусто только в ответе close(). */
  result: string | null;
};

export type ChatLibraryItem = {
  messageId: number;
  author: ChatPerson;
  ts: number;
  file: ChatFileItem;
};

export type ChatLink = {
  messageId: number;
  url: string;
  title: string;
  author: ChatPerson;
  ts: number;
};

export type ChatFileItem = {
  id: number;
  name: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  duration: number | null;
  hasPoster: boolean;
  /** Форма волны голосового: уровни 0..99, снятые при записи. */
  waveform?: number[];
};

export type ChatMessage = {
  id: number;
  channelId: string;
  author: ChatPerson;
  text: string;
  ts: number;
  parentId: number | null;
  replyCount: number;
  lastReplyAt: number | null;
  replyPeople: ChatPerson[];
  edited: boolean;
  deleted: boolean;
  /** Пост канала: показывается от имени канала, а не автора. */
  fromChannel: boolean;
  /** Вложения: несколько картинок показываются сеткой в одном пузырьке. */
  files: ChatFileItem[];
  /** Карточка видео по ссылке из текста. */
  preview: ChatVideoPreview | null;
  reactions: Record<string, ChatPerson[]>;
  /** Карточка звонка: сообщение без текста. */
  call: CallInfo | null;
  /** Ответ-цитата: на что отвечали. Оригинал удалён — остаётся `deleted`. */
  replyTo: { id: number; author: string; text: string; deleted: boolean } | null;
  /** Пересланное: имя автора оригинала; чат-источник не показываем. */
  forwardedFrom: string | null;
  /** В моей ветке под этим корнем есть непрочитанные ответы: пометка «новые». */
  newReplies: boolean;
  /** Варианты ответа кнопками под сообщением: ставит только служебный автор (Педро). */
  choices?: string[];
};

/** Непрочитанное чата, как его считает сервер: то же в списке, в ответе на прочтение и в событии `unread`. */
export type ChatUnreadInfo = Pick<ChatChannel, 'unread' | 'firstUnreadId' | 'threadRootId' | 'mentions' | 'mention'>;

export type ChatVideoPreview = {
  url: string;
  provider: string;
  providerTitle: string;
  title: string;
  author: string;
  hasThumb: boolean;
};

export type ChatSearchHit = {
  id: number;
  channelId: string;
  channelName: string;
  channelKind: string;
  author: ChatPerson | null;
  snippet: string;
  ts: number;
};

/** Событие потока: сервер шлёт их по мере жизни каналов. */
export type ChatEvent =
  | { type: 'ping' }
  | { type: 'message'; channelId: string; data: ChatMessage }
  | { type: 'message.updated'; channelId: string; data: ChatMessage }
  | { type: 'message.deleted'; channelId: string; data: { id: number } }
  | { type: 'reaction'; channelId: string; data: { id: number; reactions: ChatMessage['reactions'] } }
  | { type: 'typing'; channelId: string; data: { user: ChatPerson } }
  | {
      type: 'read';
      channelId: string;
      /** seenByAll — дочитали ли последнее сообщение канала все, кроме автора. */
      data: { userId: string; messageId: number; seenByAll: boolean };
    }
  | {
      type: 'presence';
      /** Присутствие не привязано к каналу: чат ищется по человеку. */
      channelId: string;
      data: { userId: string; online: boolean; lastSeen: number };
    }
  | { type: 'channel'; channelId: string; data: { id: string } }
  | { type: 'call'; channelId: string; data: unknown }
  | { type: 'session'; channelId: string; data: { reason: 'disabled' | 'password' } }
  | { type: 'unread'; channelId: string; data: ChatUnreadInfo }
  | { type: 'notifications'; channelId: string; data: Record<string, never> }
  | { type: 'access'; channelId: string; data: Record<string, never> }
  | { type: 'settings'; channelId: string; data: { what: 'calendars' } };

/*
 * Сколько ждём одну попытку. Здоровый сервер отвечает за доли секунды, но
 * тяжёлым выборкам (график работ, база знаний) нужен запас: восемь секунд
 * их покрывают, а дальше ждать бессмысленно — лучше начать новую попытку.
 */
const TIMEOUT_MS = 8_000;

/** Пауза перед следующей попыткой; растёт с номером попытки. */
const RETRY_PAUSE_MS = 500;

/**
 * Запрос с ограничением по времени: без него зависшее соединение (сервер
 * перезапускается, сеть отвалилась) держит экран пустым сколько угодно,
 * и повторять нечего — первая попытка так и не завершилась.
 */
async function fetchTimed(path: string, init?: RequestInit): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    return await fetch(`/api${path}`, {
      credentials: 'same-origin',
      headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
      signal: ctrl.signal,
      ...init,
    });
  } finally {
    clearTimeout(timer);
  }
}

async function once<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetchTimed(path, init);
  if (!res.ok) {
    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      /* тело не JSON */
    }
    if (res.status === 401 && !LOGIN_PATHS.includes(path)) sessionExpired?.(endReasonOf(data));
    const message = (data as { message?: string } | null)?.message ?? res.statusText;
    throw new ApiError(res.status, message);
  }
  // Nest отдаёт null/undefined пустым телом: без этой ветки res.json()
  // падал, запрос уходил на повтор, а экран показывал «нет связи»
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

/**
 * Чтение с повторами. Сетевой сбой, зависание и 5xx (сервер перезапускается
 * при деплое) не должны оставлять пустой экран: пробуем несколько раз с
 * нарастающей паузой. Ошибки 4xx не повторяются — они не про связь.
 *
 * Весь запас повторов укладывается примерно в 25 секунд: дольше человек
 * смотрит в пустой экран, не понимая, сломалось оно или думает. Не хватило
 * этих секунд — честнее показать ошибку с кнопкой повтора.
 */
async function reqRetry<T>(path: string, tries = 3, delayMs = RETRY_PAUSE_MS): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await once<T>(path);
    } catch (e) {
      const retriable = !(e instanceof ApiError) || e.status >= 500;
      // Телефон точно без сети — ждать нечего, сразу показываем ошибку;
      // «online» вернёт данные сам (useResource слушает событие).
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      if (attempt >= tries || !retriable || offline) throw e;
      await new Promise((r) => setTimeout(r, delayMs * attempt));
    }
  }
}

/**
 * Обычный запрос. Чтение (GET) тоже повторяется: половина экранов
 * приложения — это списки, и молчаливый пустой экран хуже задержки.
 * Пять попыток с растущей паузой — около десяти секунд: столько длится
 * перезапуск сервера при выкладке релиза, раньше три попытки за четыре
 * секунды в это окно не укладывались и экран показывал «нет связи».
 * Запись повторять нельзя — можно создать дубль.
 */
async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  if (method === 'GET') return reqRetry<T>(path);
  return once<T>(path, init);
}

// ---------- обходы ----------

export type FloorView = {
  id: string;
  name: string;
  orderIdx: number;
  width: number;
  height: number;
  imageAt: string; // ISO, версия картинки для адреса
  archived: boolean;
  openCount: number; // открытых замечаний на этаже
};

export type WalkView = {
  id: string;
  seq: number;
  startedBy: string;
  startedAt: string;
  finishedAt: string | null;
  autoClosed: boolean;
  note: string;
  remarkCount: number;
  hasProtocol: boolean;
};

export type RemarkView = {
  id: string;
  /** null у черновика — номер выдаётся при публикации. */
  seq: number | null;
  floorId: string;
  x: number;
  y: number;
  text: string;
  status: 'DRAFT' | 'OPEN' | 'CLOSED';
  assignee: string;
  dueDate: string | null;
  walkSeq: number | null;
  createdBy: string;
  createdAt: string;
  closedBy: string;
  closedAt: string | null;
  photoIds: number[];
};

/** Карточка замечания: к общим полям добавлено право закрытия для текущего пользователя. */
export type RemarkDetail = RemarkView & { canClose: boolean };

// ---------- календарь ----------

export type CalendarView = {
  id: string;
  kind: 'PERSONAL' | 'ROOM';
  title: string;
  ownerId: string | null;
  capacity: number | null;
  color: string;
  /** Показ у наблюдателя — личная настройка на сервере, общая для всех устройств. */
  hidden: boolean;
  level: 'OWNER' | 'MANAGE' | 'READ' | 'BUSY';
};

export type AttendeeView = {
  id: string;
  userId: string | null;
  email: string | null;
  name: string;
  optional: boolean;
  response: 'NONE' | 'YES' | 'NO' | 'MAYBE';
};

export type OccurrenceView = {
  id: string;
  calendarId: string;
  occurrence: string;
  start: string;
  end: string;
  /** Начало и конец самой серии: у одиночного события совпадают с start/end.
   * Правка меняет всю серию, поэтому форма правки берёт время отсюда, а не из
   * открытого вхождения. Приходят там же, где остальное содержимое. */
  seriesStart?: string;
  seriesEnd?: string;
  allDay: boolean;
  /** Видна только занятость: ни названия, ни участников. */
  busyOnly: boolean;
  repeats: boolean;
  /** Цвет календарного слоя: личный выбор наблюдателя, а без него — цвет
   * самого календаря (задача UI2, волна B, п.1). */
  color: string;
  repeatFreq?: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  repeatInterval?: number;
  repeatDays?: number[];
  repeatUntil?: string | null;
  title?: string;
  description?: string;
  location?: string;
  meetUrl?: string;
  callId?: string;
  /** Постоянная комната группового звонка встречи; пусто — звонка нет. */
  callRoomId?: string;
  roomId?: string | null;
  organizerId?: string;
  createdById?: string;
  organizerName?: string;
  createdByName?: string;
  visibility?: 'DEFAULT' | 'PRIVATE';
  /** Событие отменено целиком — только у вхождения, открытого напрямую по
   * ссылке или уведомлению: из окна отменённые события по-прежнему не видны. */
  cancelled?: boolean;
  /** Часовой пояс встречи (IANA), для города и сравнения с поясом смотрящего. */
  tz?: string;
  reminderMin?: number | null;
  /** Разрешено ли обычному приглашённому участнику звать в событие ещё людей. */
  attendeesCanInvite?: boolean;
  canEdit?: boolean;
  /** Право входа в постоянную комнату звонка встречи — та же мерка, что и у
   * самого входа (server/access.canEnterRoom). */
  canEnterRoom?: boolean;
  /** Состояние двери комнаты звонка, посчитанное сервером тем же расчётом, что
   * и сам вход — плашка звонка рисует по нему подпись и кнопку без своего
   * счёта времени. */
  roomDoor?: 'soon' | 'open' | 'closed';
  /** Когда откроется ближайший повтор — только при roomDoor:'soon'. */
  roomOpensAt?: string;
  myResponse?: 'NONE' | 'YES' | 'NO' | 'MAYBE';
  attendees?: AttendeeView[];
};

export type EventInput = {
  calendarId?: string;
  title: string;
  description?: string;
  location?: string;
  meetUrl?: string;
  callId?: string;
  roomId?: string | null;
  startsAt: string;
  endsAt: string;
  tz?: string;
  allDay?: boolean;
  visibility?: 'DEFAULT' | 'PRIVATE';
  reminderMin?: number | null;
  repeatFreq?: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  repeatInterval?: number;
  repeatDays?: number[];
  repeatUntil?: string | null;
  attendees?: { userId?: string; email?: string; name?: string; optional?: boolean }[];
  /** Разрешено ли обычному приглашённому участнику звать в событие ещё людей. */
  attendeesCanInvite?: boolean;
  /** Организатор просит завести постоянную комнату группового звонка встречи.
   * Учитывается только при создании — комната выдаётся один раз. */
  wantsCallRoom?: boolean;
};

/** Данные для входа в постоянную комнату группового звонка встречи. */
/** Ответ входа в постоянную комнату встречи: тот же, что у входа в обычный
 * звонок (`/chat/calls/:id/join`) — комната встречи и есть обычный звонок. */
export type EventRoomInfo = { url: string; token: string; call: ChatCall };

export type EventFile = {
  id: number;
  name: string;
  mime: string;
  size: number;
  uploadedBy: string;
  ts: number;
};

/** Проверка конфликтов перед сохранением формы встречи: занятость людей — предупреждение,
 * занятость переговорки — запрет. Не в брифе задачи 12: добавлено по сверке с контроллером,
 * так как понадобится форме создания встречи в следующей задаче. */
export type ConflictInput = {
  employeeIds?: string[];
  roomId?: string | null;
  startsAt: string;
  endsAt: string;
  /** Событие, которое сейчас правится: само себя конфликтом не считаем. */
  exceptEventId?: string;
};

export type ConflictResult = {
  /** Занятые сотрудники: предупреждение, сохранить всё равно можно. */
  busyEmployeeIds: string[];
  /** Занятость переговорки: запрет. */
  roomBusy: boolean;
};

/** Кому я выдал доступ к своему календарю и на какой ступени (задача 15). */
export type CalendarGrant = { userId: string; login: string; name: string; level: 'READ' | 'MANAGE' };

/** Переговорка справочника (задача 16): не удаляется, только скрывается (hidden). */
export type CalendarRoom = { id: string; title: string; capacity: number | null; hidden: boolean; color: string };


/** Адреса заявок по месту заведения: у каждого места свой этап на сервере. */
function paymentFormPath(s: PaymentSource): string {
  if (s.kind === 'CONTRACT') return `/payments/form/contract/${s.contractId}`;
  if (s.kind === 'COMPANY') return `/payments/form/company/${s.orgId}`;
  if (s.kind === 'OUR') return `/payments/form/our/${s.orgId}`;
  return `/projects/${s.projectId}/payment-form`;
}

function paymentCreatePath(s: PaymentSource): string {
  return s.kind === 'WORK' ? `/projects/${s.projectId}/payment-requests` : paymentFormPath(s);
}

function paymentListPath(w: PaymentListWhere): string {
  if ('contractId' in w) return `/payments/by-contract/${w.contractId}`;
  if ('recipientId' in w) return `/payments/by-recipient/${w.recipientId}`;
  if ('payerOrgId' in w) return `/payments/by-payer/${w.payerOrgId}`;
  return `/projects/${w.projectId}/payment-requests`;
}

/** Вопрос, на который помощник Педро не нашёл ответа. */
export type HelperQuestionView = {
  id: number;
  text: string;
  context: string;
  author: string;
  createdAt: string;
  state: 'open' | 'closed' | 'rejected';
  reason: string;
  closedAt: string | null;
};

export const api = {
  login: (login: string, password: string) =>
    req<Profile>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
    }),
  logout: () => req<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => req<Profile>('/auth/me'),

  avatarOwners: () => req<{ login: string; at: string }[]>('/profile/avatars'),
  /** Версия в адресе обязательна: без неё браузер покажет прежнее фото. */
  avatarUrl: (login: string, version: string) =>
    `/api/profile/avatars/${encodeURIComponent(login)}?v=${encodeURIComponent(version)}`,
  changeLogin: (login: string) =>
    req<{ login: string; pinReset: boolean }>('/profile/login', {
      method: 'PATCH',
      body: JSON.stringify({ login }),
    }),
  changePassword: (current: string, next: string) =>
    req<{ ok: true; pinReset: boolean }>('/profile/password', {
      method: 'PATCH',
      body: JSON.stringify({ current, next }),
    }),
  setBirthday: (birthday: string | null) =>
    req<{ birthday: string | null }>('/profile/birthday', {
      method: 'PATCH',
      body: JSON.stringify({ birthday }),
    }),
  uploadAvatar: async (blob: Blob): Promise<{ ok: true }> => {
    const res = await fetch(`/api/profile/avatar?mime=${encodeURIComponent(blob.type)}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: blob,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new ApiError(res.status, data.message ?? 'Не удалось сохранить фотографию');
    }
    return res.json();
  },
  clearAvatar: () => req<{ ok: true }>('/profile/avatar', { method: 'DELETE' }),

  invitePreview: (token: string) => req<InvitePreview>(`/auth/invite?token=${encodeURIComponent(token)}`),
  acceptInvite: (body: { token: string; login: string; password: string; pin?: string }) =>
    req<Profile>('/auth/invite', { method: 'POST', body: JSON.stringify(body) }),

  pinLogin: (login: string, pin: string) =>
    req<Profile>('/auth/pin-login', {
      method: 'POST',
      body: JSON.stringify({ login, pin }),
    }),
  setPin: (pin: string) =>
    req<{ user: PublicUser }>('/auth/pin', { method: 'POST', body: JSON.stringify({ pin }) }),

  calendars: () => req<CalendarView[]>('/calendar/calendars'),
  /** Личный цвет календаря (задача UI2, волна B, п.1): выбирает каждый наблюдатель
   * для себя, для любого видимого ему календаря. Сервер возвращает весь список
   * календарей заново. */
  calMyColor: (calendarId: string, color: string) =>
    req<CalendarView[]>(`/calendar/calendars/${calendarId}/color`, {
      method: 'PATCH',
      body: JSON.stringify({ color }),
    }),
  /** Личный показ календаря у наблюдателя: свойство человека, а не браузера
   * (решение Ильи 09.09.2026) — хранится на сервере, поэтому одинаково видно
   * на телефоне и на компьютере. Сервер отдаёт весь список календарей заново. */
  calMyHidden: (calendarId: string, hidden: boolean) =>
    req<CalendarView[]>(`/calendar/calendars/${calendarId}/hidden`, {
      method: 'PATCH',
      body: JSON.stringify({ hidden }),
    }),
  calEvents: (ids: string[], from: string, to: string) =>
    req<OccurrenceView[]>(
      `/calendar/events?calendars=${encodeURIComponent(ids.join(','))}&from=${from}&to=${to}`,
    ),
  /** Одно вхождение по идентификатору события (карточка по клику из уведомления, задача UI2 п.10). */
  calEventById: (id: string, occurrence?: string) =>
    req<OccurrenceView>(
      `/calendar/events/${id}${occurrence ? `?occurrence=${encodeURIComponent(occurrence)}` : ''}`,
    ),
  calConflicts: (body: ConflictInput) =>
    req<ConflictResult>('/calendar/conflicts', { method: 'POST', body: JSON.stringify(body) }),
  calCreate: (body: EventInput) =>
    req<{ id: string }>('/calendar/events', { method: 'POST', body: JSON.stringify(body) }),
  calUpdate: (id: string, body: EventInput) =>
    req<{ id: string }>(`/calendar/events/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  calCancel: (id: string) => req<{ id: string }>(`/calendar/events/${id}/cancel`, { method: 'POST' }),
  calSkip: (id: string, occurrence: string) =>
    req<{ id: string }>(`/calendar/events/${id}/skip`, {
      method: 'POST',
      body: JSON.stringify({ occurrence }),
    }),
  // «Возможно» больше не принимается сервером (решение Ильи 10.09.2026) —
  // остаются «Придёт» и «Не придёт»; уже сохранённые MAYBE в базе не трогаем,
  // карточка показывает их как «Ждём ответа» (EventCard.tsx).
  calRespond: (id: string, response: 'YES' | 'NO') =>
    req<{ id: string }>(`/calendar/events/${id}/response`, {
      method: 'POST',
      body: JSON.stringify({ response }),
    }),
  calSetAttendeeResponse: (id: string, attendeeId: string, response: 'YES' | 'NO') =>
    req<{ id: string }>(`/calendar/events/${id}/attendees/${attendeeId}/response`, {
      method: 'POST',
      body: JSON.stringify({ response }),
    }),
  /** Данные для входа в постоянную комнату группового звонка встречи (задача UI2, волна B, п.3). */
  calRoomJoin: (id: string) =>
    req<EventRoomInfo>(`/calendar/events/${id}/room/join`, { method: 'POST' }),

  // вложения встречи, по образцу вложений задачи (задача UI2, волна B, п.4)
  calEventFiles: (eventId: string) => req<EventFile[]>(`/calendar/events/${eventId}/files`),
  calEventFileUpload: async (eventId: string, file: File): Promise<EventFile> => {
    const res = await fetch(
      `/api/calendar/events/${eventId}/files?name=${encodeURIComponent(file.name)}&mime=${encodeURIComponent(file.type || 'application/octet-stream')}`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file,
      },
    );
    if (!res.ok) {
      let message = res.statusText;
      try {
        message = (await res.json()).message ?? message;
      } catch {
        /* тело не JSON */
      }
      throw new ApiError(res.status, message);
    }
    return res.json();
  },
  calEventFileUrl: (eventId: string, fileId: number) => `/api/calendar/events/${eventId}/files/${fileId}`,
  calEventFileRemove: (eventId: string, fileId: number) =>
    req<{ ok: true }>(`/calendar/events/${eventId}/files/${fileId}`, { method: 'DELETE' }),

  // доступы к своему календарю и подписочная ссылка (задача 15, личный кабинет)
  calGrants: () => req<CalendarGrant[]>('/calendar/grants'),
  calGrant: (userId: string, level: 'READ' | 'MANAGE') =>
    req<CalendarGrant[]>('/calendar/grants', {
      method: 'POST',
      body: JSON.stringify({ userId, level }),
    }),
  calRevoke: (userId: string) =>
    req<CalendarGrant[]>(`/calendar/grants/${userId}`, { method: 'DELETE' }),
  calFeedToken: () => req<{ token: string }>('/calendar/feed-token'),
  calResetFeed: () => req<{ token: string }>('/calendar/feed-token/reset', { method: 'POST' }),

  // справочник переговорных (задача 16, администрирование)
  calRooms: () => req<CalendarRoom[]>('/calendar/rooms'),
  calRoomCreate: (body: { title: string; capacity: number | null; color: string }) =>
    req<CalendarRoom[]>('/calendar/rooms', { method: 'POST', body: JSON.stringify(body) }),
  calRoomUpdate: (id: string, body: { title?: string; capacity?: number | null; hidden?: boolean }) =>
    req<CalendarRoom[]>(`/calendar/rooms/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  bootstrap: () => reqRetry<Bootstrap>('/knowledge/bootstrap'),
  process: (code: string) => req<ProcessFull>(`/knowledge/process/${encodeURIComponent(code)}`),

  comments: (thread: string, before?: number) =>
    req<{ items: CommentView[]; more: boolean; total: number; canWrite: boolean }>(
      `/comments?thread=${encodeURIComponent(thread)}${before ? `&before=${before}` : ''}`,
    ),
  addComment: (thread: string, text: string, replyTo?: number, fileId?: number) =>
    req<CommentView>('/comments', {
      method: 'POST',
      body: JSON.stringify({ thread, text, replyTo, fileId }),
    }),
  editComment: (id: number, text: string) =>
    req<CommentView>(`/comments/${id}`, { method: 'PATCH', body: JSON.stringify({ text }) }),
  deleteComment: (id: number) =>
    req<{ id: number; deleted: true }>(`/comments/${id}`, { method: 'DELETE' }),
  react: (id: number, emoji: string) =>
    req<{ id: number; reactions: CommentView['reactions'] }>(`/comments/${id}/react`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    }),
  mentionables: (thread?: string) =>
    req<{ login: string; name: string; admin?: boolean }[]>(
      `/comments/mentionables${thread ? `?thread=${encodeURIComponent(thread)}` : ''}`,
    ),
  commentUpload: async (thread: string, file: File): Promise<{ id: number; name: string; size: number }> => {
    const res = await fetch(
      `/api/comments/files?thread=${encodeURIComponent(thread)}&name=${encodeURIComponent(file.name)}&mime=${encodeURIComponent(file.type || 'application/octet-stream')}`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file,
      },
    );
    if (!res.ok) {
      let message = res.statusText;
      try {
        message = (await res.json()).message ?? message;
      } catch {
        /* тело не JSON */
      }
      throw new ApiError(res.status, message);
    }
    return res.json();
  },
  commentFileUrl: (id: number) => `/api/comments/files/${id}`,

  positions: () => reqRetry<PositionView[]>('/positions'),
  addPosition: (body: { code: string; name: string; parentId: string | null }) =>
    req<PositionView>('/positions', { method: 'POST', body: JSON.stringify(body) }),
  renamePosition: (id: string, body: { code: string; name: string }) =>
    req<PositionView>(`/positions/item/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  team: (projectId: string) => reqRetry<TeamMember[]>(`/projects/${projectId}/team`),
  teamMentionables: (projectId: string) =>
    req<{ login: string; name: string; position: string }[]>(`/projects/${projectId}/team/mentionables`),
  teamCandidates: (projectId: string, positionId: string) =>
    req<{ id: string; login: string; name: string; positions: string[] }[]>(
      `/projects/${projectId}/team/candidates?positionId=${positionId}`,
    ),
  teamAdd: (
    projectId: string,
    body: { userId: string; positionId: string; startsAt?: string | null; endsAt?: string | null },
  ) => req<TeamMember[]>(`/projects/${projectId}/team`, { method: 'POST', body: JSON.stringify(body) }),
  teamReplace: (
    projectId: string,
    memberId: string,
    body: { userId: string; startsAt?: string | null; endsAt?: string | null; moveTasks?: boolean },
  ) =>
    req<TeamMember[]>(`/projects/${projectId}/team/${memberId}/replace`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  teamRemove: (projectId: string, memberId: string) =>
    req<TeamMember[]>(`/projects/${projectId}/team/${memberId}`, { method: 'DELETE' }),
  setUserPositions: (userId: string, positions: PositionInput[]) =>
    req<UserPositionView[]>(`/positions/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ positions }),
    }),

  accessOverview: () => reqRetry<AccessOverview>('/access'),
  setMobileAccess: (items: { code: string; level: AccessMobileLevel }[]) =>
    req<AccessOverview>('/access/mobile', { method: 'PUT', body: JSON.stringify({ items }) }),
  setStageSettings: (items: { code: string; slaDays: number | null; workTimeOnly: boolean }[]) =>
    req<AccessOverview>('/access/stages', { method: 'PUT', body: JSON.stringify({ items }) }),
  setPositionAccess: (positionId: string, grants: { code: string; level: GrantLevel; scope: AccessScope }[]) =>
    req<AccessOverview>(`/access/${positionId}`, { method: 'PUT', body: JSON.stringify({ grants }) }),

  notifications: (opts: { before?: number; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.before) q.set('before', String(opts.before));
    if (opts.limit) q.set('limit', String(opts.limit));
    const tail = q.toString();
    return req<{ items: NotificationView[]; unread: number; more: boolean; firstUnreadId: number | null }>(
      `/notifications${tail ? `?${tail}` : ''}`,
    );
  },
  notifRead: (payload: { id?: number; all?: boolean; upTo?: number }) =>
    req<{ unread: number }>('/notifications/read', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** Настройки генподряда: код и день начала спринта (название, адрес и стороны ушли к зданию и исполнителю). */
  updateProject: (id: string, patch: { sprintStartDay?: number; code?: string }) =>
    req<ProjectView>(`/projects/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  // ---------- функции группы (спека 2026-09-30) ----------
  /** Работы функции, которые человеку видны: в команде или «видит все» функции. */
  works: (fn: FunctionName) => reqRetry<WorkListItem[]>(`/projects?fn=${fn}`),
  /** Плитки собственников и здания без собственника (спека 2026-10-01). */
  owners: () => reqRetry<OwnersView>('/owners'),
  /** Кем может стать собственник: наши юрлица и фонды, ещё не собственники. */
  ownerCandidates: () => reqRetry<OwnerCandidate[]>('/owners/candidates'),
  addOwner: (legalEntityId: string) =>
    req<{ id: string }>('/owners', { method: 'POST', body: JSON.stringify({ legalEntityId }) }),
  owner: (id: string) => reqRetry<OwnerCardView>(`/owners/${id}`),
  /** Здание и его работа «Собственник» внутри собственника; заводящий — в её команде. */
  addOwnerBuilding: (ownerId: string, body: { name: string; address: string; lat: number | null; lon: number | null; team: TeamSeat[] }) =>
    req<{ buildingId: string; workId: string }>(`/owners/${ownerId}/buildings`, { method: 'POST', body: JSON.stringify(body) }),
  ownerDossier: (id: string) => reqRetry<OwnerDossier>(`/owners/${id}/dossier`),
  ownerFileUrl: (id: string, fileId: string) => `/api/owners/${id}/files/${fileId}`,
  /** Здание читается и правится через работу «Собственник»: страж сужает маршрут её командой. */
  building: (ownerWorkId: string) => reqRetry<BuildingView>(`/projects/${ownerWorkId}/building`),
  updateBuilding: (ownerWorkId: string, patch: { name?: string; address?: string; lat?: number | null; lon?: number | null }) =>
    req<BuildingView>(`/projects/${ownerWorkId}/building`, { method: 'PATCH', body: JSON.stringify(patch) }),
  addPhase: (ownerWorkId: string, body: { kind: BuildingPhaseKind; startedOn: string }) =>
    req<BuildingView>(`/projects/${ownerWorkId}/building/phases`, { method: 'POST', body: JSON.stringify(body) }),
  endPhase: (ownerWorkId: string, phaseId: string, endedOn: string) =>
    req<BuildingView>(`/projects/${ownerWorkId}/building/phases/${phaseId}`, { method: 'PATCH', body: JSON.stringify({ endedOn }) }),
  closeBuilding: (ownerWorkId: string, reason: string) =>
    req<BuildingView>(`/projects/${ownerWorkId}/building/close`, { method: 'POST', body: JSON.stringify({ reason }) }),
  /** Файл досье здания; имя и тип дописывает `rawUpload`, в адресе только вид документа. */
  addBuildingFile: (ownerWorkId: string, kind: string, file: File) =>
    rawUpload<BuildingView>(`/api/projects/${ownerWorkId}/building/files?kind=${encodeURIComponent(kind)}`, file),
  buildingFileUrl: (ownerWorkId: string, fileId: string) => `/api/projects/${ownerWorkId}/building/files/${fileId}`,
  removeBuildingFile: (ownerWorkId: string, fileId: string) =>
    req<BuildingView>(`/projects/${ownerWorkId}/building/files/${fileId}`, { method: 'DELETE' }),
  /** Наши компании с видом работ функции в карточке: исполнители открываемой работы и договора функции. */
  functionPerformers: (ownerWorkId: string, fn: FunctionName) =>
    reqRetry<PerformerOption[]>(`/projects/${ownerWorkId}/performers?fn=${fn}`),
  openFunction: (ownerWorkId: string, body: { fn: FunctionName; performerId: string }) =>
    req<{ workId: string }>(`/projects/${ownerWorkId}/functions`, { method: 'POST', body: JSON.stringify(body) }),
  closeFunctionWork: (ownerWorkId: string, workId: string, reason: string) =>
    req<{ ok: true }>(`/projects/${ownerWorkId}/functions/${workId}/close`, { method: 'POST', body: JSON.stringify({ reason }) }),
  /** Закрыть свою работу изнутри (не «Собственник»): причина обязательна. */
  closeWork: (workId: string, reason: string) =>
    req<{ ok: true }>(`/projects/${workId}/close`, { method: 'POST', body: JSON.stringify({ reason }) }),
  /** Работы для поля «Работа» формы договора: открытые, куда человек номинирован; у администратора — все открытые. */
  contractWorks: () => reqRetry<WorkOption[]>('/contracts/works'),

  // asof — тестовая «машина времени» спринтов (см. sprints.controller)
  sprints: (projectId: string, asof?: string) =>
    reqRetry<SprintOverview>(`/projects/${projectId}/sprints${asof ? `?asof=${asof}` : ''}`),
  sprintBoard: (projectId: string, start: string) =>
    reqRetry<SprintBoard>(`/projects/${projectId}/sprints/board?start=${start}`),
  sprintAddTask: (projectId: string, sprintId: string, title: string, columnId?: string, asof?: string) =>
    req<SprintTaskView>(`/projects/${projectId}/sprints/${sprintId}/tasks`, {
      method: 'POST',
      body: JSON.stringify({ title, columnId, asof }),
    }),
  sprintAddColumn: (projectId: string, sprintId: string, name: string) =>
    req<{ id: string; name: string }>(`/projects/${projectId}/sprints/${sprintId}/columns`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  sprintRenameColumn: (projectId: string, columnId: string, name: string) =>
    req<{ ok: true }>(`/projects/${projectId}/sprints/columns/${columnId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  sprintMoveColumn: (projectId: string, columnId: string, index: number) =>
    req<{ ok: true }>(`/projects/${projectId}/sprints/columns/${columnId}/move`, {
      method: 'PUT',
      body: JSON.stringify({ index }),
    }),
  sprintDeleteColumn: (projectId: string, columnId: string) =>
    req<{ ok: true }>(`/projects/${projectId}/sprints/columns/${columnId}`, { method: 'DELETE' }),
  sprintMoveTask: (projectId: string, taskId: string, columnId: string, index: number) =>
    req<{ ok: true }>(`/projects/${projectId}/sprints/tasks/${taskId}/move`, {
      method: 'PUT',
      body: JSON.stringify({ columnId, index }),
    }),
  sprintUpdateTask: (
    projectId: string,
    taskId: string,
    patch: {
      title?: string;
      description?: string;
      assignee?: string;
      done?: boolean;
      attrs?: Record<string, unknown>;
    },
  ) =>
    req<SprintTaskView>(`/projects/${projectId}/sprints/tasks/${taskId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  sprintDeleteTask: (projectId: string, taskId: string) =>
    req<{ ok: true }>(`/projects/${projectId}/sprints/tasks/${taskId}`, { method: 'DELETE' }),
  project: (id: string) => reqRetry<ProjectView>(`/projects/${encodeURIComponent(id)}`),

  // ---------- строительные пакеты (строки ГПР типа «пакет») ----------
  packages: (projectId: string) => reqRetry<PackageView[]>(`/projects/${projectId}/packages`),
  package: (projectId: string, rowId: string) => req<PackageView>(`/projects/${projectId}/packages/${rowId}`),
  updatePackage: (projectId: string, rowId: string, patch: PackagePatch) =>
    req<PackageView>(`/projects/${projectId}/packages/${rowId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  finishPackage: (projectId: string, rowId: string, date?: string) =>
    req<PackageView>(`/projects/${projectId}/packages/${rowId}/finish`, { method: 'POST', body: JSON.stringify({ date }) }),
  unfinishPackage: (projectId: string, rowId: string) =>
    req<PackageView>(`/projects/${projectId}/packages/${rowId}/unfinish`, { method: 'POST', body: '{}' }),
  suspendPackage: (projectId: string, rowId: string, body: { from?: string; reason: string; comment?: string }) =>
    req<PackageView>(`/projects/${projectId}/packages/${rowId}/suspend`, { method: 'POST', body: JSON.stringify(body) }),
  resumePackage: (projectId: string, rowId: string, body: { to?: string; comment?: string }) =>
    req<PackageView>(`/projects/${projectId}/packages/${rowId}/resume`, { method: 'POST', body: JSON.stringify(body) }),
  notePackage: (projectId: string, rowId: string, text: string) =>
    req<PackageView>(`/projects/${projectId}/packages/${rowId}/note`, { method: 'POST', body: JSON.stringify({ text }) }),
  suspensionReasons: (projectId: string) => reqRetry<{ code: string; name: string }[]>(`/projects/${projectId}/packages/suspension-reasons`),
  workCategories: () => reqRetry<WorkCategory[]>('/work-categories'),
  contractors: (params: { q?: string; categoryId?: string; status?: string } = {}) =>
    reqRetry<ContractorRow[]>(`/contractors?${new URLSearchParams(params as Record<string, string>)}`),
  contractor: (id: string) => reqRetry<ContractorCardView>(`/contractors/${id}`),
  setContractorTaxMode: (id: string, body: { mode: string; at: string; vatRate?: string | number | null }) =>
    req<ContractorCardView>(`/contractors/${id}/taxmode`, { method: 'PATCH', body: JSON.stringify(body) }),
  setContractorSkills: (id: string, categoryIds: string[]) =>
    req<ContractorCardView>(`/contractors/${id}/skills`, { method: 'PATCH', body: JSON.stringify({ categoryIds }) }),
  addContractorContact: (id: string, body: { name: string; position?: string; email?: string; phone?: string; isPrimary?: boolean }) =>
    req<ContractorCardView>(`/contractors/${id}/contacts`, { method: 'POST', body: JSON.stringify(body) }),
  updateContractorContact: (
    id: string,
    contactId: string,
    body: { name?: string; position?: string; email?: string; phone?: string; isPrimary?: boolean },
  ) => req<ContractorCardView>(`/contractors/${id}/contacts/${contactId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeContractorContact: (id: string, contactId: string) =>
    req<ContractorCardView>(`/contractors/${id}/contacts/${contactId}`, { method: 'DELETE' }),
  addContractorFile: (id: string, kind: string, file: File, validTo = '') =>
    rawUpload<ContractorCardView>(
      `/api/contractors/${id}/files?kind=${encodeURIComponent(kind)}&validTo=${encodeURIComponent(validTo)}`,
      file,
    ),
  contractorFileUrl: (id: string, fileId: string) => `/api/contractors/${id}/files/${fileId}`,
  contractorChecks: (id: string) => reqRetry<ChecksView>(`/contractors/${id}/checks`),
  startContractorChecks: (id: string) => req<ChecksView>(`/contractors/${id}/checks`, { method: 'POST' }),
  /** «Готово»: заведение закончено, до этого досье, контакты и виды работ правит заводящий. */
  closeIntake: (id: string) => req<ContractorCardView>(`/contractors/${id}/intake/close`, { method: 'POST' }),
  contractorCheck: (id: string, checkId: string) => reqRetry<CheckView>(`/contractors/${id}/checks/${checkId}`),
  restartContractorCheck: (id: string, body: { kind: CheckKind; directionId?: string | null; reason: string }) =>
    req<CheckView>(`/contractors/${id}/checks/restart`, { method: 'POST', body: JSON.stringify(body) }),
  refreshCheck: (id: string, checkId: string) =>
    req<CheckView>(`/contractors/${id}/checks/${checkId}/refresh`, { method: 'POST', body: '{}' }),
  setCheckItem: (
    id: string,
    checkId: string,
    code: string,
    body: { state: FactorState; comment?: string; overrideReason?: string },
  ) =>
    req<CheckView>(`/contractors/${id}/checks/${checkId}/items/${code}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  addCheckCommentFile: (id: string, checkId: string, commentId: string, file: File) =>
    rawUpload<{ id: string; name: string; mime: string; size: number }>(
      `/api/contractors/${id}/checks/${checkId}/comments/${commentId}/files`,
      file,
    ),
  checkFileUrl: (id: string, checkId: string, fileId: string) => `/api/contractors/${id}/checks/${checkId}/files/${fileId}`,
  /** Отчёт службы: выжимка страницей и исходник файлом. */
  reportUrl: (id: string, reportId: string, format: 'html' | 'json' | 'pdf' = 'html') =>
    `/api/contractors/${id}/reports/${reportId}${format === 'html' ? '' : `?format=${format}`}`,
  addCheckComment: (id: string, checkId: string, itemId: string, text: string) =>
    req<CheckComment>(`/contractors/${id}/checks/${checkId}/items/${itemId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  finishCheck: (id: string, checkId: string, verdict: string) =>
    req<CheckView>(`/contractors/${id}/checks/${checkId}/finish`, {
      method: 'POST',
      body: JSON.stringify({ verdict }),
    }),
  workCalendar: () => reqRetry<CalendarYearView[]>('/admin/calendar'),
  criteria: (params: { kind?: string; directionId?: string; includeHidden?: string } = {}) =>
    reqRetry<CriterionView[]>(`/admin/criteria?${new URLSearchParams(params as Record<string, string>)}`),
  addCriterion: (body: Partial<CriterionView>) =>
    req<CriterionView>('/admin/criteria', { method: 'POST', body: JSON.stringify(body) }),
  updateCriterion: (id: string, body: Partial<CriterionView>) =>
    req<CriterionView>(`/admin/criteria/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeCriterion: (id: string) => req<{ ok: boolean }>(`/admin/criteria/${id}`, { method: 'DELETE' }),
  slaReport: (period: 'month' | 'quarter' | 'year') => reqRetry<SlaReport>(`/process-catalog/report?period=${period}`),
  myAssignments: () => reqRetry<{ taken: AssignmentView[]; queue: AssignmentView[] }>('/assignments/mine'),
  assignment: (id: string) => reqRetry<AssignmentView>(`/assignments/${id}`),
  takeAssignment: (id: string) => req<AssignmentView>(`/assignments/${id}/take`, { method: 'POST' }),
  removeContractorFile: (id: string, fileId: string) =>
    req<ContractorCardView>(`/contractors/${id}/files/${fileId}`, { method: 'DELETE' }),
  addWorkCategory: (body: { code: string; name: string; parentId: string }) =>
    req<WorkCategory[]>('/work-categories', { method: 'POST', body: JSON.stringify(body) }),
  updateWorkCategory: (id: string, body: { name?: string; hidden?: boolean }) =>
    req<WorkCategory[]>(`/work-categories/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeWorkCategory: (id: string) => req<WorkCategory[]>(`/work-categories/${id}`, { method: 'DELETE' }),
  packageSections: (projectId: string) => reqRetry<PackageSection[]>(`/projects/${projectId}/packages/sections`),
  gpr: (id: string) => reqRetry<{ data: unknown; updatedAt: string }>(`/projects/${encodeURIComponent(id)}/gpr`),
  saveGpr: (id: string, plan: unknown) =>
    req<{ ok: true; updatedAt: string }>(`/projects/${encodeURIComponent(id)}/gpr`, {
      method: 'PUT',
      body: JSON.stringify(plan),
    }),

  taskFeed: (projectId: string, rowId: string) =>
    req<TaskFeedItem[]>(`/projects/${projectId}/rows/${encodeURIComponent(rowId)}/feed`),
  taskPost: (
    projectId: string,
    rowId: string,
    body: {
      text: string;
      kind?: 'comment' | 'event';
      taskName?: string;
      notify?: string[];
      replyTo?: number;
      fileId?: number;
    },
  ) =>
    req<TaskFeedItem>(`/projects/${projectId}/rows/${encodeURIComponent(rowId)}/feed`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  taskEditComment: (projectId: string, rowId: string, id: number, text: string) =>
    req<TaskFeedItem>(`/projects/${projectId}/rows/${encodeURIComponent(rowId)}/feed/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ text }),
    }),
  taskReact: (projectId: string, rowId: string, id: number, emoji: string) =>
    req<TaskFeedItem>(`/projects/${projectId}/rows/${encodeURIComponent(rowId)}/feed/${id}/react`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    }),
  taskDeleteComment: (projectId: string, rowId: string, id: number) =>
    req<{ ok: true }>(`/projects/${projectId}/rows/${encodeURIComponent(rowId)}/feed/${id}`, {
      method: 'DELETE',
    }),
  taskFiles: (projectId: string, rowId: string) =>
    req<TaskFileItem[]>(`/projects/${projectId}/rows/${encodeURIComponent(rowId)}/files`),
  taskUpload: async (projectId: string, rowId: string, file: File): Promise<TaskFileItem> => {
    const res = await fetch(
      `/api/projects/${projectId}/rows/${encodeURIComponent(rowId)}/files?name=${encodeURIComponent(file.name)}&mime=${encodeURIComponent(file.type || 'application/octet-stream')}`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file,
      },
    );
    if (!res.ok) {
      let message = res.statusText;
      try {
        message = (await res.json()).message ?? message;
      } catch {
        /* тело не JSON */
      }
      throw new ApiError(res.status, message);
    }
    return res.json();
  },
  taskFileUrl: (projectId: string, rowId: string, fileId: number) =>
    `/api/projects/${projectId}/rows/${encodeURIComponent(rowId)}/files/${fileId}`,
  taskDeleteFile: (projectId: string, rowId: string, fileId: number) =>
    req<{ ok: true }>(`/projects/${projectId}/rows/${encodeURIComponent(rowId)}/files/${fileId}`, {
      method: 'DELETE',
    }),

  // ---------- чат ----------

  // в чате ждать десять секунд нечестно: пробуем трижды и быстро сознаёмся
  chatChannels: () => reqRetry<ChatChannel[]>('/chat/channels'),
  chatPeople: () => req<(ChatPerson & { online?: boolean })[]>('/chat/people'),
  chatCreateChannel: (body: {
    kind: ChatKind;
    name: string;
    topic?: string;
    isPrivate?: boolean;
    memberIds?: string[];
  }) => req<{ id: string }>('/chat/channels', { method: 'POST', body: JSON.stringify(body) }),
  chatProfile: (id: string) => req<ChatProfile>(`/chat/channels/${id}/profile`),
  chatPerson: (userId: string) => req<ChatPersonCard>(`/chat/people/${userId}`),

  // ---------- звонки ----------

  callCurrent: () => req<ChatCall | null>('/chat/calls/current'),
  callStart: (input: { userId?: string; userIds?: string[]; channelId?: string; kind: 'audio' | 'video' }) =>
    req<ChatCall & { offline?: boolean }>('/chat/calls', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  callInvite: (id: string, userIds: string[]) =>
    req<ChatCall>(`/chat/calls/${id}/invite`, { method: 'POST', body: JSON.stringify({ userIds }) }),
  /** Пропуск в комнату медиасервера: живёт два часа, берётся заново при каждом входе. */
  callJoin: (id: string, tabId = '') =>
    req<{ url: string; token: string; call: ChatCall }>(`/chat/calls/${id}/join`, {
      method: 'POST',
      body: JSON.stringify({ tabId }),
    }),
  callDecline: (id: string) => req<{ ok: true }>(`/chat/calls/${id}/decline`, { method: 'POST' }),
  /** `successorId` — кому уходящий ведущий передаёт роль; остальным не нужен. */
  callLeave: (id: string, successorId?: string) =>
    req<{ ok: true }>(`/chat/calls/${id}/leave`, {
      method: 'POST',
      body: JSON.stringify(successorId ? { successorId } : {}),
    }),
  /** Отсчёт в шапке: минуты или `null`, чтобы снять. */
  callTimer: (id: string, minutes: number | null) =>
    req<{ ok: true }>(`/chat/calls/${id}/timer`, { method: 'POST', body: JSON.stringify({ minutes }) }),
  callHost: (id: string, userId: string) =>
    req<{ ok: true }>(`/chat/calls/${id}/host`, { method: 'POST', body: JSON.stringify({ userId }) }),
  /** Право показа экрана: выдаёт ведущий; пусто — вернуть себе. */
  callScreenShare: (id: string, participantId: string | null) =>
    req<{ ok: true }>(`/chat/calls/${id}/screen-share`, { method: 'POST', body: JSON.stringify({ participantId }) }),
  /** Удалить гостя: право администратора звонка. */
  callKick: (id: string, participantId: string) =>
    req<{ ok: true }>(`/chat/calls/${id}/kick`, { method: 'POST', body: JSON.stringify({ participantId }) }),
  /** «Завершить для всех»: право администратора звонка. */
  callClose: (id: string) => req<{ ok: true }>(`/chat/calls/${id}/close`, { method: 'POST' }),

  // ---------- доска в звонке ----------

  /** Текущий (открытый) лист целиком, включая штрихи; null — листа нет. */
  sheetCurrent: (callId: string) => req<SheetView | null>(`/chat/calls/${callId}/sheet`),
  /** Открыть чистый лист: право ведущего. */
  sheetOpenBlank: (callId: string, w: number, h: number) =>
    req<SheetView>(`/chat/calls/${callId}/sheet?kind=blank&w=${w}&h=${h}`, { method: 'POST' }),
  /** Открыть лист из страницы документа: картинка сырым телом, как подложка этажа обхода. */
  sheetOpenPage: async (
    callId: string,
    png: Blob,
    w: number,
    h: number,
    sourceName: string,
    sourcePage: number,
  ): Promise<SheetView> => {
    const qs = new URLSearchParams({
      kind: 'page',
      w: String(w),
      h: String(h),
      sourceName,
      sourcePage: String(sourcePage),
    });
    const res = await fetch(`/api/chat/calls/${callId}/sheet?${qs}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: png,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new ApiError(res.status, data.message ?? 'Не удалось открыть лист');
    }
    return (await res.json()) as SheetView;
  },
  /* --- переписка внутри звонка (спека 2026-09-11) --- */
  callMessages: (callId: string) => req<CallMessage[]>(`/chat/calls/${callId}/messages`),
  callMessagePost: (callId: string, text: string, fileIds: number[] = []) =>
    req<CallMessage>(`/chat/calls/${callId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ text, fileIds }),
    }),
  /** Вложение сырым телом, как подложка листа доски; номер файла идёт потом в сообщение. */
  callMessageUpload: (callId: string, file: File) =>
    rawUpload<CallFile>(`/api/chat/calls/${callId}/messages/file`, file),
  callMessageFileUrl: (callId: string, fileId: number) =>
    `/api/chat/calls/${callId}/messages/file/${fileId}`,

  roomMessages: (token: string) => req<CallMessage[]>(`/rooms/${token}/messages`),
  roomMessagePost: (token: string, text: string, fileIds: number[] = []) =>
    req<CallMessage>(`/rooms/${token}/messages`, {
      method: 'POST',
      body: JSON.stringify({ text, fileIds }),
    }),
  roomMessageUpload: (token: string, file: File) =>
    rawUpload<CallFile>(`/api/rooms/${token}/messages/file`, file),
  roomMessageFileUrl: (token: string, fileId: number) =>
    `/api/rooms/${token}/messages/file/${fileId}`,

  /** Закрыть текущий лист: право ведущего, закрытие — это и сохранение. */
  sheetClose: (callId: string) => req<SheetView>(`/chat/calls/${callId}/sheet/close`, { method: 'POST' }),
  /** Дописать штрих целиком, по завершении жеста. Короткий ответ — лист клиент уже нарисовал у себя,
   *  но с номером созданного штриха: клиент подставляет его вместо временного (0), иначе ластик по
   *  свежему штриху до следующего полного чтения листа ничего на сервере не находит. */
  sheetStroke: (
    callId: string,
    stroke: { color: string; width: number; pts: { x: number; y: number }[]; shape?: unknown },
  ) =>
    req<{ id: number }>(`/chat/calls/${callId}/sheet/stroke`, { method: 'POST', body: JSON.stringify(stroke) }),
  /** Снять свой последний штрих (кнопка «Отменить»). */
  sheetUndo: (callId: string) => req<{ ok: true }>(`/chat/calls/${callId}/sheet/undo`, { method: 'POST' }),
  /** Стереть свой конкретный штрих (попадание ластика) — чужие штрихи не трогает. */
  sheetErase: (callId: string, strokeId: number) =>
    req<{ ok: true }>(`/chat/calls/${callId}/sheet/erase`, { method: 'POST', body: JSON.stringify({ strokeId }) }),
  /** Передвинуть или растянуть свою фигуру — чужие не трогает. */
  sheetMove: (callId: string, strokeId: number, pts: { x: number; y: number }[], shape: unknown) =>
    req<{ ok: true }>(`/chat/calls/${callId}/sheet/move`, {
      method: 'POST',
      body: JSON.stringify({ strokeId, pts, shape }),
    }),

  // ---------- комната по ссылке ----------

  /** Дверь комнаты: единственный запрос, который делает гость без учётной записи. */
  roomDoor: (token: string) =>
    req<{
      state: 'soon' | 'open' | 'closed';
      title: string;
      host: string;
      opensAt: number | null;
      /** Кем сервер узнал спрашивающего: `null` — надо представиться. */
      me: 'user' | 'guest' | null;
    }>(`/rooms/${encodeURIComponent(token)}`),
  roomJoin: (token: string, name: string) =>
    req<{ participantId: string; url: string; token: string; call: ChatCall }>(
      `/rooms/${encodeURIComponent(token)}/join`,
      { method: 'POST', body: JSON.stringify({ name }) },
    ),
  roomLeave: (token: string) =>
    req<{ ok: true }>(`/rooms/${encodeURIComponent(token)}/leave`, { method: 'POST' }),

  // ---------- доска в звонке, гостевые ----------
  // Открытия и закрытия у гостя нет — листами распоряжается только ведущий (всегда сотрудник).

  roomSheetCurrent: (token: string) => req<SheetView | null>(`/rooms/${encodeURIComponent(token)}/sheet`),
  roomSheetStroke: (
    token: string,
    stroke: { color: string; width: number; pts: { x: number; y: number }[]; shape?: unknown },
  ) =>
    req<{ id: number }>(`/rooms/${encodeURIComponent(token)}/sheet/stroke`, {
      method: 'POST',
      body: JSON.stringify(stroke),
    }),
  /** То же для гостя: свои фигуры он двигает наравне со всеми. */
  roomSheetMove: (token: string, strokeId: number, pts: { x: number; y: number }[], shape: unknown) =>
    req<{ ok: true }>(`/rooms/${encodeURIComponent(token)}/sheet/move`, {
      method: 'POST',
      body: JSON.stringify({ strokeId, pts, shape }),
    }),
  roomSheetUndo: (token: string) =>
    req<{ ok: true }>(`/rooms/${encodeURIComponent(token)}/sheet/undo`, { method: 'POST' }),
  roomSheetErase: (token: string, strokeId: number) =>
    req<{ ok: true }>(`/rooms/${encodeURIComponent(token)}/sheet/erase`, {
      method: 'POST',
      body: JSON.stringify({ strokeId }),
    }),

  /** Итоги звонка для сотрудника: файлы, накопленные к его выходу из звонка. */
  callSummary: (callId: string) =>
    req<{ files: { id: string; name: string; size: number }[]; expired: boolean }>(
      `/chat/calls/${encodeURIComponent(callId)}/summary`,
    ),
  /** Скачивание одного файла итога — ссылка, не запрос: у файла своя отдача (Content-Disposition). */
  callFileUrl: (callId: string, sheetId: string) =>
    `/api/chat/calls/${encodeURIComponent(callId)}/sheet/result?v=${encodeURIComponent(sheetId)}`,

  /** Итоги доски для гостя (раздел 7.3 спеки): файлы, накопленные к его выходу. */
  roomFiles: (token: string) =>
    req<{ files: { id: string; name: string; size: number }[]; expired: boolean }>(
      `/rooms/${encodeURIComponent(token)}/files`,
    ),
  /** Скачивание одного файла итога — ссылка, не запрос: у файла своя отдача (Content-Disposition). */
  roomFileUrl: (token: string, sheetId: string) =>
    `/api/rooms/${encodeURIComponent(token)}/sheet-files/${encodeURIComponent(sheetId)}`,

  chatUpdateProfile: (id: string, body: { name?: string; topic?: string }) =>
    req<{ id: string }>(`/chat/channels/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  chatSetRole: (id: string, userId: string, role: 'admin' | 'member') =>
    req<{ userId: string; role: string }>(`/chat/channels/${id}/role`, {
      method: 'POST',
      body: JSON.stringify({ userId, role }),
    }),
  chatLibrary: (id: string, tab: 'media' | 'voice' | 'files') =>
    req<{ items: ChatLibraryItem[] }>(`/chat/channels/${id}/library?tab=${tab}`),
  chatLinks: (id: string) => req<{ items: ChatLink[] }>(`/chat/channels/${id}/library?tab=links`),
  chatAvatarUrl: (id: string, at: string) =>
    `/api/chat/channels/${id}/avatar?v=${encodeURIComponent(at)}`,
  chatSetAvatar: async (id: string, blob: Blob): Promise<{ ok: true }> => {
    const res = await fetch(`/api/chat/channels/${id}/avatar?mime=${encodeURIComponent(blob.type)}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: blob,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new ApiError(res.status, data.message ?? 'Не удалось сохранить картинку');
    }
    return res.json();
  },
  chatClearAvatar: (id: string) =>
    req<{ ok: true }>(`/chat/channels/${id}/avatar`, { method: 'DELETE' }),
  chatOpenDm: (userId: string) =>
    req<{ id: string }>('/chat/dm', { method: 'POST', body: JSON.stringify({ userId }) }),
  chatMembers: (id: string) =>
    req<(ChatPerson & { role: string })[]>(`/chat/channels/${id}/members`),
  chatRemoveMember: (id: string, userId: string) =>
    req<{ id: string }>(`/chat/channels/${id}/members/${userId}`, { method: 'DELETE' }),
  chatFolders: () => req<ChatFolder[]>('/chat/folders'),
  chatCreateFolder: (name: string) =>
    req<ChatFolder>('/chat/folders', { method: 'POST', body: JSON.stringify({ name }) }),
  chatDeleteFolder: (id: string) =>
    req<{ ok: true }>(`/chat/folders/${id}`, { method: 'DELETE' }),
  chatSetFolder: (id: string, folderId: string | null) =>
    req<{ ok: true }>(`/chat/channels/${id}/folder`, {
      method: 'POST',
      body: JSON.stringify({ folderId }),
    }),
  chatHide: (id: string, hidden: boolean) =>
    req<{ ok: true }>(`/chat/channels/${id}/hide`, {
      method: 'POST',
      body: JSON.stringify({ hidden }),
    }),
  chatRead: (id: string, messageId: number) =>
    req<{ id: string } & ChatUnreadInfo>(`/chat/channels/${id}/read`, {
      method: 'POST',
      body: JSON.stringify({ messageId }),
    }),
  chatReadMarks: (id: string) =>
    req<(ChatPerson & { messageId: number })[]>(`/chat/channels/${id}/reads`),
  /** Голосовые этой переписки, которые человек уже слушал. */
  chatVoicePlayed: (id: string) => req<number[]>(`/chat/channels/${id}/voice-plays`),
  chatMarkVoicePlayed: (fileId: number) =>
    req<{ ok: true }>(`/chat/files/${fileId}/voice-play`, { method: 'POST' }),
  /** `from` открывает ленту от указанного сообщения, а не с конца переписки. */
  chatMessages: (id: string, before?: number, from?: number) => {
    const q = new URLSearchParams();
    if (before) q.set('before', String(before));
    else if (from) q.set('from', String(from));
    const tail = q.toString();
    return reqRetry<{ items: ChatMessage[]; more: boolean }>(
      `/chat/channels/${id}/messages${tail ? `?${tail}` : ''}`,
      3,
      700,
    );
  },
  chatPost: (
    id: string,
    body: { text?: string; parentId?: number; fileIds?: number[]; replyToId?: number; clientKey?: string },
  ) =>
    req<ChatMessage>(`/chat/channels/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Пересылка: копии сообщений уходят сразу в несколько чатов. */
  chatForward: (messageIds: number[], toChannelIds: string[]) =>
    req<{ sent: number }>('/chat/forward', {
      method: 'POST',
      body: JSON.stringify({ messageIds, toChannelIds }),
    }),
  /** Закрепить сообщение в чате; messageId = null снимает закрепление. */
  chatPin: (id: string, messageId: number | null) =>
    req<{ id: string; pinnedMessageId: number | null }>(`/chat/channels/${id}/pin`, {
      method: 'POST',
      body: JSON.stringify({ messageId }),
    }),
  chatTyping: (id: string) =>
    req<{ ok: true }>(`/chat/channels/${id}/typing`, { method: 'POST' }),
  chatThread: (messageId: number) =>
    req<{ root: ChatMessage; items: ChatMessage[] }>(`/chat/messages/${messageId}/thread`),
  /** Прочитано в ветке до ответа; в ответ — пересчитанное непрочитанное чата. */
  chatThreadRead: (rootId: number, messageId: number) =>
    req<{ channelId: string } & ChatUnreadInfo>(`/chat/threads/${rootId}/read`, {
      method: 'POST',
      body: JSON.stringify({ messageId }),
    }),
  chatEdit: (messageId: number, text: string) =>
    req<ChatMessage>(`/chat/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ text }),
    }),
  chatDelete: (messageId: number) =>
    req<{ id: number; deleted: true }>(`/chat/messages/${messageId}`, { method: 'DELETE' }),
  chatReact: (messageId: number, emoji: string) =>
    req<{ id: number; reactions: ChatMessage['reactions'] }>(`/chat/messages/${messageId}/react`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    }),
  chatSearch: (q: string, channelId?: string) =>
    req<{ items: ChatSearchHit[] }>(
      `/chat/search?q=${encodeURIComponent(q)}${channelId ? `&channelId=${channelId}` : ''}`,
    ),
  chatFileUrl: (channelId: string, fileId: number) => `/api/chat/channels/${channelId}/files/${fileId}`,
  chatPreviewThumbUrl: (url: string) => `/api/chat/preview?url=${encodeURIComponent(url)}`,
  chatPosterUrl: (channelId: string, fileId: number) =>
    `/api/chat/channels/${channelId}/files/${fileId}/poster`,
  chatSetPoster: async (channelId: string, fileId: number, blob: Blob): Promise<void> => {
    await fetch(
      `/api/chat/channels/${channelId}/files/${fileId}/poster?mime=${encodeURIComponent(blob.type || 'image/webp')}`,
      {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: blob,
      },
    ).catch(() => {});
  },
  chatUpload: async (
    channelId: string,
    file: File,
    meta: {
      duration?: number;
      width?: number;
      height?: number;
      waveform?: number[];
      /* Доля отправленного, 0..1. Нужен XMLHttpRequest: fetch о ходе отправки
         молчит, а на большом файле это выглядит как зависание. */
      onProgress?: (fraction: number) => void;
    } = {},
  ): Promise<ChatFileItem> => {
    const params = new URLSearchParams({
      name: file.name,
      mime: file.type || 'application/octet-stream',
    });
    if (meta.duration) params.set('duration', String(meta.duration));
    if (meta.width) params.set('width', String(meta.width));
    if (meta.height) params.set('height', String(meta.height));
    if (meta.waveform?.length) {
      params.set('waveform', meta.waveform.map((v) => Math.round(v * 99)).join(','));
    }
    const url = `/api/chat/channels/${channelId}/files?${params.toString()}`;
    const { onProgress } = meta;
    return new Promise<ChatFileItem>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(e.loaded / e.total);
        };
      }
      xhr.onload = () => {
        let data: unknown = null;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          /* сервер ответил не JSON — сообщение возьмём по умолчанию */
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(1);
          resolve(data as ChatFileItem);
          return;
        }
        const message =
          (data as { message?: string } | null)?.message ?? 'Не удалось загрузить файл';
        reject(new ApiError(xhr.status, message));
      };
      xhr.onerror = () => reject(new ApiError(0, 'Сеть недоступна'));
      xhr.onabort = () => reject(new ApiError(0, 'Отправка прервана'));
      xhr.send(file);
    });
  },

  pushKey: () => req<{ publicKey: string | null }>('/push/key'),
  pushSubscribe: (body: { endpoint: string; p256dh: string; auth: string }) =>
    req<{ ok: boolean }>('/push/subscribe', { method: 'POST', body: JSON.stringify(body) }),
  pushUnsubscribe: (endpoint: string) =>
    req<{ ok: boolean }>('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),

  users: () => req<AdminUser[]>('/users'),
  /** Заводит сотрудника и высылает приглашение; ссылка возвращается сразу. */
  addUser: (u: {
    firstName: string;
    lastName: string;
    email: string;
    isAdmin?: boolean;
    positions?: PositionInput[];
  }) =>
    req<{ ok: true; id: string; link: string; sent: boolean }>('/users', {
      method: 'POST',
      body: JSON.stringify(u),
    }),
  reinvite: (id: string) =>
    req<{ link: string; sent: boolean }>(`/users/${id}/invite`, { method: 'POST' }),
  editUser: (login: string, u: Partial<AdminUser> & { password?: string }) =>
    req<{ ok: true }>(`/users/${encodeURIComponent(login)}`, {
      method: 'PATCH',
      body: JSON.stringify(u),
    }),
  deleteUser: (login: string) =>
    req<{ ok: true }>(`/users/${encodeURIComponent(login)}`, { method: 'DELETE' }),

  // ---------- обходы ----------

  floors: (projectId: string) => req<FloorView[]>(`/projects/${projectId}/floors`),
  floorCreate: (projectId: string, name: string) =>
    req<FloorView>(`/projects/${projectId}/floors`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  floorUpdate: (
    projectId: string,
    id: string,
    patch: { name?: string; orderIdx?: number; archived?: boolean },
  ) =>
    req<FloorView>(`/projects/${projectId}/floors/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  floorImageUpload: async (projectId: string, floorId: string, png: Blob, w: number, h: number) => {
    const res = await fetch(
      `/api/projects/${projectId}/floors/${floorId}/image?mime=image/png&w=${w}&h=${h}`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: png,
      },
    );
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new ApiError(res.status, data.message ?? 'Не удалось загрузить план этажа');
    }
    return (await res.json()) as FloorView;
  },
  floorImageUrl: (projectId: string, floorId: string, v: string) =>
    `/api/projects/${projectId}/floors/${floorId}/image?v=${encodeURIComponent(v)}`,
  floorDelete: (projectId: string, id: string) =>
    req<{ ok: true }>(`/projects/${projectId}/floors/${id}`, { method: 'DELETE' }),

  walks: (projectId: string) => req<WalkView[]>(`/projects/${projectId}/walks`),
  walkActive: (projectId: string) => req<WalkView | null>(`/projects/${projectId}/walks/active`),
  walkStart: (projectId: string) => req<WalkView>(`/projects/${projectId}/walks`, { method: 'POST' }),
  walkFinish: (projectId: string, id: string, note?: string) =>
    req<WalkView>(`/projects/${projectId}/walks/${id}/finish`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
  walkProtocolUrl: (projectId: string, id: string) => `/api/projects/${projectId}/walks/${id}/protocol`,
  openReportUrl: (projectId: string) => `/api/projects/${projectId}/walks/open-report`,

  remarks: (projectId: string, floorId?: string, status?: 'DRAFT' | 'OPEN' | 'CLOSED') => {
    const params = new URLSearchParams();
    if (floorId) params.set('floorId', floorId);
    if (status) params.set('status', status);
    const qs = params.toString();
    return req<RemarkView[]>(`/projects/${projectId}/remarks${qs ? `?${qs}` : ''}`);
  },
  remark: (projectId: string, id: string) => req<RemarkDetail>(`/projects/${projectId}/remarks/${id}`),
  remarkDraft: (
    projectId: string,
    body: { clientId: string; floorId: string; x: number; y: number; text: string },
  ) =>
    req<RemarkView>(`/projects/${projectId}/remarks`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  remarkPhotoUpload: async (projectId: string, id: string, jpeg: Blob, w: number, h: number) => {
    // Ярлык — настоящий тип файла, а не константа: compressImage пропускает
    // HEIC/GIF/файлы без типа как есть (canCompress отвергает всё, кроме
    // jpeg/png/webp), и жёсткий "image/jpeg" отправлял такой файл серверу
    // под чужим ярлыком. Сервер верит ярлыку — пусть теперь честно отвергает
    // не-JPEG сразу, а не молча принимает нечитаемые байты.
    const mime = jpeg.type || 'application/octet-stream';
    const res = await fetch(
      `/api/projects/${projectId}/remarks/${id}/photos?mime=${mime}&w=${w}&h=${h}`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: jpeg,
      },
    );
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new ApiError(res.status, data.message ?? 'Не удалось отправить фотографию');
    }
    return (await res.json()) as RemarkView;
  },
  remarkPhotoUrl: (projectId: string, id: string, photoId: number) =>
    `/api/projects/${projectId}/remarks/${id}/photos/${photoId}`,
  remarkPhotoDelete: (projectId: string, id: string, photoId: number) =>
    req<RemarkView>(`/projects/${projectId}/remarks/${id}/photos/${photoId}`, { method: 'DELETE' }),
  remarkDeleteDraft: (projectId: string, id: string) =>
    req<{ ok: true }>(`/projects/${projectId}/remarks/${id}`, { method: 'DELETE' }),
  remarkPublish: (projectId: string, id: string, body: { assignee?: string; dueDate?: string } = {}) =>
    req<RemarkView>(`/projects/${projectId}/remarks/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  remarkUpdate: (
    projectId: string,
    id: string,
    patch: { text?: string; assignee?: string; dueDate?: string | null; x?: number; y?: number },
  ) =>
    req<RemarkView>(`/projects/${projectId}/remarks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  remarkClose: (projectId: string, id: string) =>
    req<RemarkView>(`/projects/${projectId}/remarks/${id}/close`, { method: 'POST' }),
  remarkReopen: (projectId: string, id: string) =>
    req<RemarkView>(`/projects/${projectId}/remarks/${id}/reopen`, { method: 'POST' }),

  serverHistory: (days: 1 | 7 | 30 | 365) => req<ServerSampleView[]>(`/health/history?days=${days}`),
  serverStorage: () => req<StorageView>('/health/storage'),

  // ---------- внешние сервисы ----------
  services: () => req<ServiceView[]>('/services'),
  // Ключ целиком приходит только сюда, отдельным запросом по нажатию «Показать» —
  // не вместе со списком (задача 16.09.2026).
  serviceReveal: (code: string) => req<{ value: string }>(`/services/${code}/reveal`),
  serviceSave: (code: string, value: string) =>
    req<CheckResult>(`/services/${code}`, { method: 'PUT', body: JSON.stringify({ value }) }),
  serviceCheck: (code: string) => req<CheckResult>(`/services/${code}/check`, { method: 'POST' }),

  // ---------- вопросы помощнику (администрирование) ----------
  /** Педро для окна «Написать сотруднику»; null — помощник выключен. */
  helperMascot: () => req<ChatPerson | null>('/helper/mascot'),
  helperQuestions: () => req<HelperQuestionView[]>('/helper/questions'),
  closeHelperQuestion: (id: number) => req<{ ok: true }>(`/helper/questions/${id}/close`, { method: 'POST' }),
  rejectHelperQuestion: (id: number, reason: string) =>
    req<{ ok: true }>(`/helper/questions/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),

  // ---------- заявки на оплату и реестр (спека 2026-10-07) ----------
  paymentForm: (s: PaymentSource) => req<PaymentForm>(paymentFormPath(s)),
  createPaymentRequest: (s: PaymentSource, input: PaymentRequestInput) =>
    req<PaymentRequestCard>(paymentCreatePath(s), { method: 'POST', body: JSON.stringify(input) }),
  paymentRequest: (id: string) => req<PaymentRequestCard>(`/payments/requests/${id}`),
  updatePaymentRequest: (id: string, input: PaymentRequestInput) =>
    req<PaymentRequestCard>(`/payments/requests/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  submitPaymentRequest: (id: string) => req<PaymentRequestCard>(`/payments/requests/${id}/submit`, { method: 'POST' }),
  withdrawPaymentRequest: (id: string) => req<PaymentRequestCard>(`/payments/requests/${id}/withdraw`, { method: 'POST' }),
  cancelPaymentRequest: (id: string, reason: string) =>
    req<PaymentRequestCard>(`/payments/requests/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
  /** Счёт или прочий файл заявки; имя и тип дописывает `rawUpload`. */
  addPaymentFile: (id: string, kind: 'INVOICE' | 'OTHER', file: File) =>
    rawUpload<PaymentRequestCard>(`/api/payments/requests/${id}/files?kind=${kind}`, file),
  removePaymentFile: (id: string, fileId: string) =>
    req<PaymentRequestCard>(`/payments/requests/${id}/files/${fileId}`, { method: 'DELETE' }),
  paymentFileUrl: (id: string, fileId: string) => `/api/payments/requests/${id}/files/${fileId}`,
  paymentRequests: (where: PaymentListWhere) => req<PaymentRequestView[]>(paymentListPath(where)),
  paymentRegisters: () => req<PaymentRegisterRow[]>('/payments/registers'),
  paymentRegister: (id: string) => req<PaymentRegisterView>(`/payments/registers/${id}`),
  paymentUpcoming: () => req<PaymentUpcomingRow[]>('/payments/registers/upcoming'),
  paymentUpcomingDay: (day: string) => req<PaymentUpcomingView>(`/payments/registers/upcoming/${day}`),
  decidePaymentLine: (id: string, step: PaymentStep, requestId: string, decision: PaymentDecision, note = '') =>
    req<PaymentRegisterView>(`/payments/registers/${id}/${step === 'FIN' ? 'fin' : 'ceo'}/lines/${requestId}`, {
      method: 'POST',
      body: JSON.stringify({ decision, note }),
    }),
  passPaymentRegister: (id: string) => req<PaymentRegisterView>(`/payments/registers/${id}/fin/pass`, { method: 'POST' }),
  approveRestPaymentRegister: (id: string) =>
    req<PaymentRegisterView>(`/payments/registers/${id}/ceo/approve-rest`, { method: 'POST' }),
  paymentRegisterXlsxUrl: (id: string) => `/api/payments/registers/${id}/xlsx`,
  paymentSettings: () => req<PaymentSettingsView>('/payments/settings'),
  savePaymentSettings: (body: { weekdays: number[]; cutoff: string; invoiceLimit: string }) =>
    req<PaymentSettingsView>('/payments/settings', { method: 'PUT', body: JSON.stringify(body) }),

  // ---------- модель генпроектировщика ----------
  modelVersions: (projectId: string) => req<ModelVersionView[]>(`/projects/${projectId}/model/versions`),
  /** Подписанная ссылка на загрузку файла модели в хранилище. */
  modelUploadStart: (projectId: string) =>
    req<{ storageKey: string; uploadUrl: string }>(`/projects/${projectId}/model/versions/start`, { method: 'POST' }),
  modelUploadFinish: (
    projectId: string,
    body: { storageKey: string; size: number; sourceName: string; checks: ModelPlaceCheck[] },
  ) => req<ModelVersionView>(`/projects/${projectId}/model/versions`, { method: 'POST', body: JSON.stringify(body) }),
  /** Подписанная ссылка на скачивание файла версии. */
  modelVersionUrl: (projectId: string, versionId: string) =>
    req<{ url: string }>(`/projects/${projectId}/model/versions/${versionId}/url`),
  modelRemarks: (projectId: string) => req<ModelRemarkView[]>(`/projects/${projectId}/model/remarks`),
  modelRemarkCreate: (projectId: string, body: ModelPointInput & { text: string; assignee: string; dueDate?: string | null }) =>
    req<ModelRemarkView>(`/projects/${projectId}/model/remarks`, { method: 'POST', body: JSON.stringify(body) }),
  modelRemarkUpdate: (projectId: string, id: string, patch: { text?: string; assignee?: string; dueDate?: string | null }) =>
    req<ModelRemarkView>(`/projects/${projectId}/model/remarks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  /** Шаг замечания; note — причина закрытия или комментарий к возврату в работу. */
  modelRemarkMove: (projectId: string, id: string, move: ModelRemarkMove, note?: string) =>
    req<ModelRemarkView>(`/projects/${projectId}/model/remarks/${id}/${move}`, {
      method: 'POST',
      body: JSON.stringify(move === 'close' ? { reason: note } : move === 'return' ? { comment: note } : {}),
    }),
  modelRemarkPlaceOk: (projectId: string, id: string) =>
    req<ModelRemarkView>(`/projects/${projectId}/model/remarks/${id}/place-ok`, { method: 'POST' }),
  modelRemarkReplace: (projectId: string, id: string, body: ModelPointInput) =>
    req<ModelRemarkView>(`/projects/${projectId}/model/remarks/${id}/replace`, { method: 'POST', body: JSON.stringify(body) }),

  // ---------- юрлица ----------
  legalEntities: () => reqRetry<LegalEntityView[]>('/legal'),
  /** Юрлица для выбора: краткое имя для проекта, полное для договора. */
  legalOptions: () => reqRetry<LegalOption[]>('/legal/options'),
  /** Роли наших юрлиц для чипов — всякому вошедшему; читает `lib/ourRoles`. */
  ourRoles: () => reqRetry<OurRoles>('/legal/roles'),
  lookupLegalParty: (inn: string) => req<LegalLookup>(`/legal/lookup?inn=${encodeURIComponent(inn)}`),
  addLegalEntity: (body: LegalEntityInput) =>
    req<LegalCreated>('/legal', { method: 'POST', body: JSON.stringify(body) }),
  updateLegalEntity: (id: string, patch: Partial<LegalEntityInput>) =>
    req<LegalEntityView>(`/legal/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  removeLegalEntity: (id: string) => req<{ ok: true }>(`/legal/${id}`, { method: 'DELETE' }),

  // ---------- фонды ----------
  /** Кем может быть УК фонда — из «Юрлиц группы» или «Контрагентов». */
  fundManagers: (scope: 'legal' | 'counterparties') => reqRetry<ManagerOption[]>(`/${scope}/managers`),
  addGroupFund: (input: FundInput) => req<LegalEntityView>('/legal/funds', { method: 'POST', body: JSON.stringify(input) }),
  updateGroupFund: (id: string, patch: FundInput) =>
    req<LegalEntityView>(`/legal/${id}/fund`, { method: 'PATCH', body: JSON.stringify(patch) }),
  addExternalFund: (input: FundInput) =>
    req<CounterpartyCardView>('/counterparties/funds', { method: 'POST', body: JSON.stringify(input) }),
  updateExternalFund: (orgId: string, patch: FundInput) =>
    req<CounterpartyCardView>(`/counterparties/${orgId}/fund`, { method: 'PATCH', body: JSON.stringify(patch) }),
  /** Новая редакция документа фонда: вид и подписи в строке запроса, тело — файл (по образцу addContractorFile). */
  addFundFile: (
    scope: 'legal' | 'counterparties', id: string, file: File, kind: FundDocKindName, regNo: string, registeredAt: string,
  ) =>
    rawUpload<LegalEntityView | CounterpartyCardView>(
      `/api/${scope}/${id}/fund/files?kind=${encodeURIComponent(kind)}&regNo=${encodeURIComponent(regNo)}&registeredAt=${encodeURIComponent(registeredAt)}`,
      file,
    ),
  fundFileUrl: (scope: 'legal' | 'counterparties', id: string, fileId: string) =>
    `/api/${scope}/${id}/fund/files/${fileId}`,
  addFundContact: (scope: 'legal' | 'counterparties', id: string, body: { name: string; position?: string; email?: string; phone?: string; isPrimary?: boolean }) =>
    req<LegalEntityView | CounterpartyCardView>(`/${scope}/${id}/fund/contacts`, { method: 'POST', body: JSON.stringify(body) }),
  updateFundContact: (
    scope: 'legal' | 'counterparties', id: string, contactId: string,
    body: { name?: string; position?: string; email?: string; phone?: string; isPrimary?: boolean },
  ) => req<LegalEntityView | CounterpartyCardView>(`/${scope}/${id}/fund/contacts/${contactId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeFundContact: (scope: 'legal' | 'counterparties', id: string, contactId: string) =>
    req<LegalEntityView | CounterpartyCardView>(`/${scope}/${id}/fund/contacts/${contactId}`, { method: 'DELETE' }),

  // ---------- контрагенты ----------
  counterparties: () => reqRetry<CounterpartyRow[]>('/counterparties'),
  counterparty: (orgId: string) => reqRetry<CounterpartyCardView>(`/counterparties/${orgId}`),
  counterpartyParty: (orgId: string) =>
    reqRetry<{ party: PartyLookup['party']; enabled: boolean }>(`/counterparties/${orgId}/party`),
  lookupCounterparty: (inn: string) => req<CounterpartyLookup>(`/counterparties/lookup?inn=${encodeURIComponent(inn)}`),
  addCounterparty: (body: { inn: string; name?: string; roles: StoredRole[] }) =>
    req<CounterpartyCardView>('/counterparties', { method: 'POST', body: JSON.stringify(body) }),
  setCounterpartyRoles: (orgId: string, roles: StoredRole[]) =>
    req<CounterpartyCardView>(`/counterparties/${orgId}/roles`, { method: 'PATCH', body: JSON.stringify({ roles }) }),
  /** Карточка без проверки: заводится при первом открытии, повтор отдаёт ту же. */
  ensureCompanyCard: (orgId: string) => req<{ profileId: string }>(`/counterparties/${orgId}/card`, { method: 'POST' }),

  // ---------- договоры ----------
  /** Договоры и сколько скрыто сужением по работам; пустые ключи фильтра не передаются. */
  contracts: (filter: { profileId?: string; orgId?: string; projectId?: string } = {}) => {
    const clean = Object.fromEntries(Object.entries(filter).filter(([, v]) => v)) as Record<string, string>;
    return reqRetry<{ rows: ContractRow[]; hidden: number }>(`/contracts?${new URLSearchParams(clean)}`);
  },
  contract: (id: string) => reqRetry<ContractCardView>(`/contracts/${id}`),
  /** Стороны для формы нового договора: под этапом «Инициировать договор», а не под «Контрагентами». */
  contractParties: () => reqRetry<CounterpartyRow[]>('/contracts/parties'),
  /**
   * Договор заводится вместе с первой версией: реквизиты в адресе, файл
   * телом. Название места первого файла едет здесь же (`title`), названия
   * остальных — списком (`titles`), а их файлы уезжают следом по одному:
   * тело запроса занято самим файлом, и разом их не отправить. Заведение и
   * есть открытие круга 1 — договор из одного файла уходит в комментирование
   * этим же запросом, договор с приложениями открывает круг последним файлом.
   */
  addContract: (input: ContractInput, file: File, title = '') =>
    rawUpload<ContractCardView>(
      `/api/contracts?${new URLSearchParams({
        counterpartyId: input.counterpartyId ?? '',
        ourSide: input.ourSide ?? '',
        type: input.type,
        subjectText: input.subjectText,
        parentId: input.parentId ?? '',
        amendKind: input.amendKind ?? '',
        amendsId: input.amendsId ?? '',
        // Объявленные изменения строкой JSON: список одним параметром строки
        // запроса не разложить, как и названия частей.
        amendChanges: JSON.stringify(input.amendChanges ?? []),
        legalEntityId: input.legalEntityId,
        projectId: input.projectId,
        categoryId: input.categoryId,
        closing: input.closing,
        framework: input.framework ? '1' : '0',
        currency: input.currency,
        // Налогов, которых форма не шлёт, в строке запроса нет вовсе.
        ...(input.vatRate === undefined ? {} : { vatRate: String(input.vatRate) }),
        ...(input.taxMode === undefined ? {} : { taxMode: input.taxMode }),
        ...(input.taxModeAt === undefined ? {} : { taxModeAt: input.taxModeAt }),
        amount: input.amount,
        validTo: input.validTo,
        form: input.form,
        // Календарь платежей строкой JSON: в строке запроса таблице места нет.
        payments: JSON.stringify(input.payments),
        title,
        // Названия остальных частей строкой JSON: список одним параметром
        // строки запроса не разложить.
        titles: JSON.stringify(input.titles ?? []),
        package: JSON.stringify(input.package ?? []),
        ukMode: input.ukMode ?? '',
      })}`,
      file,
    ),
  /**
   * Отправка старого черновика на комментирование. У нового договора этого
   * шага нет: круг 1 открывается при заведении (решение Ильи 22.09.2026).
   */
  sendContract: (id: string) => req<ContractCardView>(`/contracts/${id}/send`, { method: 'POST' }),
  /*
   * Версия это набор мест, и файлы уезжают по одному: тело запроса занято
   * самим файлом. Место называется в адресе, заводится новым названием
   * (только в черновике) или не называется вовсе — тогда раскладку
   * подставляет сервер и показывает её в `nextVersion` до отправки.
   */
  addContractVersionFile: (
    id: string,
    file: File,
    target: { placeId?: string; title?: string } = {},
    /** Доля отправленного, 0…1: по ней в окне версии крутится кольцо хода. */
    onProgress?: (fraction: number) => void,
  ) =>
    rawUpload<ContractCardView>(
      `/api/contracts/${id}/versions?${new URLSearchParams({
        place: target.placeId ?? '',
        title: target.title ?? '',
      })}`,
      file,
      onProgress,
    ),
  /** Файл набора в другое место: раскладку подставил сервер, правит человек. */
  moveContractVersionFile: (id: string, fileId: string, placeId: string) =>
    req<ContractCardView>(`/contracts/${id}/versions/${fileId}`, {
      method: 'PATCH',
      body: JSON.stringify({ placeId }),
    }),
  removeContractVersionFile: (id: string, fileId: string) =>
    req<ContractCardView>(`/contracts/${id}/versions/${fileId}`, { method: 'DELETE' }),
  /** Набор уходит кругом целиком: неполный сервер не пустит. */
  sendContractVersion: (id: string) =>
    req<ContractCardView>(`/contracts/${id}/versions/send`, { method: 'POST' }),
  contractVersionUrl: (id: string, versionId: string) => `/api/contracts/${id}/versions/${versionId}`,
  closeContractComments: (id: string) => req<ContractCardView>(`/contracts/${id}/close-comments`, { method: 'POST' }),
  contractToVisas: (id: string) => req<ContractCardView>(`/contracts/${id}/to-visas`, { method: 'POST' }),
  contractVisa: (id: string, ok: boolean, reason = '') =>
    req<ContractCardView>(`/contracts/${id}/visa`, { method: 'POST', body: JSON.stringify({ ok, reason }) }),
  /** Номер договора руками: договору любой формы, ушедшему с виз без номера, вписывает держатель. */
  contractFinal: (id: string, patch: { number?: string }) =>
    req<ContractCardView>(`/contracts/${id}/final`, { method: 'POST', body: JSON.stringify(patch) }),
  /**
   * Способ подписания и номер: выбираются на визировании одним шагом этапа
   * «Выбрать способ подписания и присвоить номер» (решение Ильи 02.10.2026).
   */
  contractSignWay: (
    id: string,
    ask: { signing: string; number?: string; numberSource?: 'OURS' | 'UK'; packageNumbers?: Record<string, string> },
  ) => req<ContractCardView>(`/contracts/${id}/sign-way`, { method: 'POST', body: JSON.stringify(ask) }),
  contractSign: (id: string, ok: boolean, reason = '') =>
    req<ContractCardView>(`/contracts/${id}/sign`, { method: 'POST', body: JSON.stringify({ ok, reason }) }),
  contractScan: (id: string, number: string, date: string, file: File) =>
    rawUpload<ContractCardView>(
      `/api/contracts/${id}/scan?${new URLSearchParams({ number, date })}`,
      file,
    ),
  cancelContract: (id: string, reason: string) =>
    req<ContractCardView>(`/contracts/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),

  /*
   * УК в согласовании договора фонда (часть 4): ответ по кругу, скан подписи
   * и ссылка представителю УК — ход держателя, этап «Вести договор».
   */
  /** Ответ УК по кругу: без замечаний — без файла, с замечаниями — с файлом. */
  contractUkReply: (id: string, remarks: boolean, file: File | null) => {
    const qs = new URLSearchParams({ remarks: remarks ? '1' : '0' });
    return file
      ? rawUpload<ContractCardView>(`/api/contracts/${id}/uk/reply?${qs}`, file)
      : req<ContractCardView>(`/contracts/${id}/uk/reply?${qs}`, { method: 'POST' });
  },
  /** Скан, подписанный УК: PDF телом запроса, как скан договора. */
  contractUkSign: (id: string, file: File) => rawUpload<ContractCardView>(`/api/contracts/${id}/uk/sign`, file),
  contractUkReturn: (id: string, reason: string) =>
    req<ContractCardView>(`/contracts/${id}/uk/return`, { method: 'POST', body: JSON.stringify({ reason }) }),
  closeContractUkGuest: (id: string) => req<ContractCardView>(`/contracts/${id}/uk/guest/close`, { method: 'POST' }),
  contractUkFileUrl: (id: string, stepId: string) => `/api/contracts/${id}/uk/${stepId}`,

  /** Системные пакеты сервера: пока только преобразование договоров в PDF. */
  serverDeps: () => req<{ converter: 'ok' | 'missing' }>('/health/deps'),

  // ---------- договоры: документ и замечания ----------
  /** PDF версии для просмотра в окне; версии без PDF сервер отвечает 404. */
  contractPdfUrl: (id: string, versionId: string) => `/api/contracts/${id}/versions/${versionId}/pdf`,
  contractComments: (id: string) => reqRetry<ContractCommentView[]>(`/contracts/${id}/comments`),
  addContractComment: (id: string, body: ContractCommentInput) =>
    req<ContractCommentView>(`/contracts/${id}/comments`, { method: 'POST', body: JSON.stringify(body) }),
  /** Один маршрут правки: видимость меняет автор, ответ по существу — держатель. */
  patchContractComment: (
    id: string,
    commentId: string,
    body: { visibility?: CommentVisibility; resolution?: CommentResolution; note?: string },
  ) => req<ContractCommentView>(`/contracts/${id}/comments/${commentId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** Docx с примечаниями — по одному месту: примечания живут внутри файла. */
  contractExportUrl: (id: string, placeId: string) =>
    `/api/contracts/${id}/export.docx?${new URLSearchParams({ place: placeId })}`,
  contractCompare: (id: string, from: string, to: string) =>
    reqRetry<ContractCompareView>(`/contracts/${id}/compare?${new URLSearchParams({ from, to })}`),

  /* ---------- договор по гостевой ссылке ----------
   * Единственные маршруты без учётной записи: сеанс гостя — кука, которую
   * ставит `guestPassEnter`, и живёт она только на путях своей же ссылки. Поэтому
   * токен здесь в каждом вызове, а не запоминается один раз. */
  guestContract: (token: string) => reqRetry<GuestContractView>(`/g/contracts/${g(token)}`),
  guestPass: (pass: string) => req<GuestPassState>(`/g/p/${g(pass)}`),
  guestPassEnter: (pass: string) => req<{ url: string }>(`/g/p/${g(pass)}`, { method: 'POST' }),
  guestPassNew: (pass: string) => req<{ email: string }>(`/g/p/${g(pass)}/new`, { method: 'POST' }),
  guestVersions: (token: string) => reqRetry<GuestContractVersion[]>(`/g/contracts/${g(token)}/versions`),
  guestPdfUrl: (token: string, versionId: string) => `/api/g/contracts/${g(token)}/versions/${versionId}/pdf`,
  guestComments: (token: string) => reqRetry<ContractCommentView[]>(`/g/contracts/${g(token)}/comments`),
  addGuestComment: (token: string, body: ContractCommentInput) =>
    req<ContractCommentView>(`/g/contracts/${g(token)}/comments`, { method: 'POST', body: JSON.stringify(body) }),
  /** Ответ представителя УК по кругу: только по ссылке УК и только в комментировании. */
  guestUkAnswer: (token: string, answer: 'OK' | 'REMARKS') =>
    req<GuestContractView>(`/g/contracts/${g(token)}/uk-answer`, { method: 'POST', body: JSON.stringify({ answer }) }),

  /* ---------- приглашённые к договору: распоряжается держатель ----------
   * Отвечают свежей карточкой: список приглашённых живёт в ней, и отдельного
   * чтения после нажатия не нужно. Приглашение и «Выслать ещё раз» отдают ещё
   * итог письма: ушло или ссылка на экране. Убрать приглашённого — закрыть
   * доступ ему одному на всех устройствах; закрыть всем — `closeContractGuest`. */
  inviteContractGuest: (id: string, body: ContractInviteInput) =>
    req<ContractInviteResult>(`/contracts/${id}/invites`, { method: 'POST', body: JSON.stringify(body) }),
  resendContractInvite: (id: string, inviteId: string) =>
    req<ContractInviteResult>(`/contracts/${id}/invites/${inviteId}/resend`, { method: 'POST' }),
  removeContractInvite: (id: string, inviteId: string) =>
    req<ContractCardView>(`/contracts/${id}/invites/${inviteId}`, { method: 'DELETE' }),
  closeContractGuest: (id: string) => req<ContractCardView>(`/contracts/${id}/guest/close`, { method: 'POST' }),
  removeContractGuest: (id: string, guestId: string) =>
    req<ContractCardView>(`/contracts/${id}/guests/${guestId}`, { method: 'DELETE' }),
};

/** Токен приходит из адресной строки: в путь он идёт только закодированным. */
function g(token: string): string {
  return encodeURIComponent(token);
}

// ---------- мониторинг сервера ----------

/*
 * Байты в базе — `BigInt` (размер базы перерастает `Int` на двух
 * гигабайтах), а `BigInt` в JSON не сериализуется — сервер отдаёт такие
 * поля строкой. Отсюда `string`, а не `bigint`, у всех полей размера, в том
 * числе у показателей кластера, которых может не быть вовсе (`null`).
 */
export type ServerSampleView = {
  at: string;
  partial: boolean;
  cpuAvg: number;
  cpuMax: number;
  memUsed: string;
  memTotal: string;
  vmDiskUsed: string;
  vmDiskTotal: string;
  dbSize: string;
  dbConns: number;
  dbConnMax: number;
  dbTps: number | null;
  dbCacheHit: number | null;
  dbDeadlocks: number | null;
  clusterDiskUsed: string | null;
  clusterDiskTotal: string | null;
  clusterCpu: number | null;
  clusterMem: string | null;
  requests: number;
  errors: number;
  avgMs: number;
  maxMs: number;
  streams: number;
};

export type StorageLineView = {
  // отказ своего источника на сервере — не ноль, а «не удалось узнать»
  used: string | null;
  total: string | null;
  perDay: number | null;
  fullAt: string | null;
};

export type StorageView = {
  cluster: StorageLineView | null;
  db: StorageLineView;
  vm: StorageLineView;
  tables: { name: string; size: string }[];
};

// ---------- внешние сервисы ----------

/**
 * Откуда взят доступ: ключ из базы, ключ из настроек машины, доступ от самой
 * машины без всякого ключа (бывает только у Yandex Cloud), или доступа нет.
 */
export type KeyAccess = 'db' | 'env' | 'vm' | 'none';

/** Значение ключа сюда не входит: список его не отдаёт, только `serviceReveal`. */
export type ServiceView = {
  code: string;
  title: string;
  purpose: string;
  hasKey: boolean;
  /** Хвост ключа в несколько символов — отличить один от другого, не показывая целиком. */
  hint: string;
  access: KeyAccess;
  checkedAt: string | null;
  checkOk: boolean | null;
  checkNote: string;
};

export type CheckResult = { ok: boolean; note: string };

// ---------- заявки на оплату и реестр (спека 2026-10-07) ----------
// Копия видов server/src/payments/payments.service.ts и registers.service.ts.

export type PaymentRequestState = 'DRAFT' | 'SUBMITTED' | 'IN_REGISTER' | 'APPROVED' | 'RETURNED' | 'REJECTED' | 'CANCELLED';
export type PaymentDecision = 'APPROVE' | 'RETURN' | 'REJECT' | 'MOVE';
export type PaymentStep = 'FIN' | 'CEO';
export type PaymentRegisterState = 'FIN' | 'CEO' | 'APPROVED';

/** Откуда заводят: договор, карточка компании (она получатель), наша компания (она платит), работа функции. */
export type PaymentSource =
  | { kind: 'CONTRACT'; contractId: string }
  | { kind: 'COMPANY'; orgId: string }
  | { kind: 'OUR'; orgId: string }
  | { kind: 'WORK'; projectId: string };

export type PaymentHint = { month: number | null; total: number };
export type PaymentParty = { id: string; name: string };

export type PaymentContractOption = {
  id: string;
  label: string;
  payerId: string;
  payerName: string;
  recipientId: string;
  recipientName: string;
  vatRate: number;
  /** Почему по договору заявку не подать; пусто — можно. */
  note: string;
  plan: { id: string; orderIdx: number; date: string; amount: number; label: string; regular: boolean }[];
  total: number;
  requests: { id: string; amount: number; desiredDate: string; state: PaymentRequestState }[];
};

export type PaymentForm = {
  source: PaymentSource;
  payer: PaymentParty | null;
  recipient: PaymentParty | null;
  contracts: PaymentContractOption[];
  invoice: boolean;
  /** `inn` — ИНН компании, нет — пустая строка: по нему ищут в поле. */
  payers: (PaymentParty & { inn: string })[];
  recipients: (PaymentParty & { checked: boolean; inn: string })[];
  today: string;
};

export type PaymentRequestInput = {
  contractId: string;
  paymentId: string;
  payerId: string;
  recipientId: string;
  amount: string;
  vatRate: number;
  purpose: string;
  desiredDate: string;
};

export type PaymentRequestView = {
  id: string;
  seq: number;
  kind: 'CONTRACT' | 'INVOICE' | 'RECURRING';
  source: 'CONTRACT' | 'COMPANY' | 'OUR' | 'WORK' | 'REGULAR';
  state: PaymentRequestState;
  stateLabel: string;
  moved: boolean;
  payer: { id: string; orgId: string; name: string; fund: boolean };
  recipient: { id: string; name: string; inn: string };
  contract: { id: string; label: string } | null;
  payment: { id: string; label: string } | null;
  projectId: string | null;
  amount: string;
  vatRate: number;
  vat: string;
  purpose: string;
  desiredDate: string;
  payDay: string | null;
  registerId: string | null;
  author: string;
  authorName: string;
  createdAt: string;
  submittedAt: string | null;
  files: { id: string; kind: 'INVOICE' | 'OTHER'; name: string; size: number }[];
  note: string;
  mine: boolean;
  can: { edit: boolean; submit: boolean; withdraw: boolean; cancel: boolean };
};

export type PaymentRequestEvent = {
  id: string;
  kind: string;
  step: string;
  decision: string;
  text: string;
  actor: string;
  actorName: string;
  createdAt: string;
};

export type PaymentRequestCard = PaymentRequestView & { hint: PaymentHint | null; events: PaymentRequestEvent[] };

/** Где лежит список: договор, получатель, кто платит (наше юрлицо), работа или все заявки группы. */
export type PaymentListWhere =
  | { contractId: string }
  | { recipientId: string }
  | { payerOrgId: string }
  | { projectId: string };

export type PaymentRegisterRow = {
  id: string;
  payDay: string;
  state: PaymentRegisterState;
  stateLabel: string;
  count: number;
  total: number;
};

/** Будущий реестр: поданные заявки одного платёжного дня, ещё не собранные. */
export type PaymentUpcomingRow = { payDay: string; count: number; total: number; cutoffDay: string; cutoffTime: string };

/** Заявка строкой таблицы реестра: с работой для колонки и фильтра «Проект». */
export type PaymentTableLine = PaymentRequestView & { project: { id: string; name: string } | null };

export type PaymentUpcomingView = { payDay: string; cutoffDay: string; cutoffTime: string; lines: PaymentTableLine[] };

export type PaymentRegisterLine = PaymentTableLine & {
  fin: PaymentDecision | null;
  ceo: PaymentDecision | null;
  finNote: string;
  ceoNote: string;
};

export type PaymentRegisterView = {
  id: string;
  payDay: string;
  state: PaymentRegisterState;
  stateLabel: string;
  passedBy: string;
  passedAt: string | null;
  approvedBy: string;
  approvedAt: string | null;
  lines: PaymentRegisterLine[];
  task: { id: string; userId: string | null; userName: string } | null;
  canPass: boolean;
};

/** Настройки платежей: дни недели 1–7, отсечка «ЧЧ:ММ» по Москве, предел в рублях. */
export type PaymentSettingsView = { weekdays: number[]; cutoff: string; invoiceLimit: number };

// ---------- модель генпроектировщика (зеркало server/src/design-model/design-model.service.ts) ----------

/** Вид камеры просмотрщика: откуда и куда смотрит. */
export type ModelCamera = { position: [number, number, number]; target: [number, number, number] };

export type ModelVersionView = { id: string; number: number; size: number; sourceName: string; uploadedBy: string; uploadedAt: string };

export type ModelRemarkStatus = 'OPEN' | 'FIXED' | 'CLOSED';
export type ModelRemarkMove = 'fix' | 'close' | 'return' | 'reopen';

export type ModelRemarkView = {
  id: string;
  seq: number;
  text: string;
  status: ModelRemarkStatus;
  x: number;
  y: number;
  z: number;
  elementGuid: string;
  elementName: string;
  camera: ModelCamera;
  versionId: string;
  assignee: string;
  assigneeOk: boolean;
  dueDate: string | null;
  createdBy: string;
  createdAt: string;
  fixedBy: string;
  fixedAt: string | null;
  closedBy: string;
  closedAt: string | null;
  closeReason: string;
  /** Место после новой версии: элемент удалён или сдвинут. */
  placeChanged: '' | 'REMOVED' | 'MOVED';
  can: { fix: boolean; manage: boolean };
};

/** Точка на модели и вид камеры, с которых поставлено или переставлено замечание. */
export type ModelPointInput = {
  x: number;
  y: number;
  z: number;
  elementGuid?: string;
  elementName?: string;
  elementX?: number;
  elementY?: number;
  elementZ?: number;
  camera: ModelCamera;
  versionId: string;
};

/** Проверка места замечания на новой модели: нашёлся ли элемент и где он теперь. */
export type ModelPlaceCheck = { remarkId: string; found: boolean; x?: number; y?: number; z?: number };
